import { useState, useEffect } from 'react';
import { signOut, onAuthStateChanged } from 'firebase/auth';
import { collection, addDoc, getDocs, updateDoc, deleteDoc, doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { useNavigate } from 'react-router-dom';
import { LogOut, ArrowLeft } from 'lucide-react';
import ImageUpload from '../components/ImageUpload';
import VideoUpload from '../components/VideoUpload';
import { 
  sanitizeString, 
  sanitizeURL, 
  sanitizeYouTubeURL, 
  sanitizeArray, 
  sanitizeNumber,
  validateServiceData,
  validateStatData,
  validateTrainerData
} from '../utils/sanitize';
import './Admin.css';

function Admin() {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('main');
  const [activeSection, setActiveSection] = useState(null);
  const [activeGroupSection, setActiveGroupSection] = useState(null);
  const [activeMobileSection, setActiveMobileSection] = useState(null);
  const navigate = useNavigate();

  // State for different sections
  const [announcement, setAnnouncement] = useState({ active: false, message: '', linkUrl: '', linkText: '', scrollToSection: '' });
  const [hero, setHero] = useState({ title: '', subtitle: '', buttonText: '' });
  const [growthWays, setGrowthWays] = useState([]);
  const [services, setServices] = useState([]);
  const [stats, setStats] = useState([]);
  const [about, setAbout] = useState({ title: '', content: '', certifications: [], imageUrl: '' });
  const [gallery, setGallery] = useState([]);
  const [youtubeVideo, setYoutubeVideo] = useState({ url: '', title: '' });
  const [pricing, setPricing] = useState([]);
  const [contact, setContact] = useState({ title: '', subtitle: '' });
  const [footer, setFooter] = useState({ copyright: '' });
  const [trainers, setTrainers] = useState([]);
  const [editingTrainerId, setEditingTrainerId] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [trainerForm, setTrainerForm] = useState({
    name: '', specialty: '', bio: '', experience: '', certifications: '', imageUrl: '', calendlyUrl: ''
  });
  
  // New service form
  const [showNewServiceForm, setShowNewServiceForm] = useState(false);
  const [newService, setNewService] = useState({ title: '', description: '' });
  
  // New stat form
  const [showNewStatForm, setShowNewStatForm] = useState(false);
  const [newStat, setNewStat] = useState({ number: '', label: '' });
  
  // New pricing form
  const [showNewPricingForm, setShowNewPricingForm] = useState(false);
  const [newPricing, setNewPricing] = useState({ title: '', price: '', period: '', features: '', calendlyUrl: '', learnMoreUrl: '', featured: false });
  
  // Locations
  const [locations, setLocations] = useState([]);
  const [showNewLocationForm, setShowNewLocationForm] = useState(false);
  const [newLocation, setNewLocation] = useState({ name: '', address: '', description: '', hours: '', features: '', mapLink: '', featured: false });
  const [serviceAreas, setServiceAreas] = useState({ title: '', description: '', onlineNotice: '' });
  
  // Mobile Training
  const [mobileTraining, setMobileTraining] = useState({ title: '', subtitle: '', description: '', imageUrl: '', features: '' });
  
  // Group Page States
  const [groupHero, setGroupHero] = useState({ title: '', subtitle: '', buttonText: '' });
  const [groupVideo, setGroupVideo] = useState({ url: '', title: '' });
  const [groupWhoFor, setGroupWhoFor] = useState({ title: '', content: '', imageUrl: '' });
  const [groupPackages, setGroupPackages] = useState([]);
  const [groupSchedule, setGroupSchedule] = useState({ address: '', timeDescription: '' });
  const [groupFinalCTA, setGroupFinalCTA] = useState({ title: '', subtitle: '', calendlyUrl: '' });
  const [showNewGroupPackageForm, setShowNewGroupPackageForm] = useState(false);
  const [newGroupPackage, setNewGroupPackage] = useState({ title: '', price: '', description: '', features: '', calendlyUrl: '', order: 0 });
  const [groupGallery, setGroupGallery] = useState([]);
  
  // Mobile Page States
  const [mobilePageHero, setMobilePageHero] = useState({ title: '', subtitle: '', buttonText: '' });
  const [mobilePageVideo, setMobilePageVideo] = useState({ url: '', title: '' });
  const [mobilePageWhoFor, setMobilePageWhoFor] = useState({ title: '', content: '', imageUrl: '' });
  const [mobilePackages, setMobilePackages] = useState([]);
  const [mobilePageFinalCTA, setMobilePageFinalCTA] = useState({ title: '', subtitle: '', calendlyUrl: '' });
  const [showNewMobilePackageForm, setShowNewMobilePackageForm] = useState(false);
  const [newMobilePackage, setNewMobilePackage] = useState({ title: '', price: '', description: '', features: '', calendlyUrl: '', order: 0 });
  const [mobileGallery, setMobileGallery] = useState([]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser) {
        fetchAllData();
      } else {
        navigate('/login');
      }
      setLoading(false);
    });

    return unsubscribe;
  }, [navigate]);

  // Debug: Monitor mobilePackages state changes
  useEffect(() => {
    console.log('🟡 MOBILE PACKAGES STATE CHANGED:', {
      length: mobilePackages.length,
      packages: mobilePackages,
      isArray: Array.isArray(mobilePackages)
    });
  }, [mobilePackages]);

  // Handle tab scroll indicators
  useEffect(() => {
    const tabsContainer = document.querySelector('.admin-tabs');
    if (!tabsContainer) return;

    const handleScroll = () => {
      const scrollLeft = tabsContainer.scrollLeft;
      const maxScroll = tabsContainer.scrollWidth - tabsContainer.clientWidth;

      if (scrollLeft > 10) {
        tabsContainer.classList.add('scrolled-left');
      } else {
        tabsContainer.classList.remove('scrolled-left');
      }

      if (scrollLeft < maxScroll - 10) {
        tabsContainer.classList.add('scrolled-right');
      } else {
        tabsContainer.classList.remove('scrolled-right');
      }
    };

    // Initial check
    handleScroll();

    tabsContainer.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleScroll);

    return () => {
      tabsContainer.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, []);

  const fetchAllData = async () => {
    console.log('🔴 FETCH ALL DATA - START');
    
    // Wrap each fetch in try-catch so one failure doesn't stop others
    
    // Fetch announcement
    try {
      const announcementDoc = await getDoc(doc(db, 'settings', 'announcement'));
      if (announcementDoc.exists()) setAnnouncement(announcementDoc.data());
    } catch (error) {
      console.error('❌ Error fetching announcement:', error.message);
    }

    // Fetch hero
    try {
      const heroDoc = await getDoc(doc(db, 'settings', 'hero'));
      if (heroDoc.exists()) setHero(heroDoc.data());
    } catch (error) {
      console.error('❌ Error fetching hero:', error.message);
    }

    // Fetch growth ways
    try {
      const growthSnapshot = await getDocs(collection(db, 'growthWays'));
      setGrowthWays(growthSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })).sort((a, b) => a.order - b.order));
    } catch (error) {
      console.error('❌ Error fetching growth ways:', error.message);
    }

    // Fetch services
    try {
      const servicesSnapshot = await getDocs(collection(db, 'services'));
      setServices(servicesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })).sort((a, b) => a.order - b.order));
    } catch (error) {
      console.error('❌ Error fetching services:', error.message);
    }

    // Fetch stats
    try {
      const statsSnapshot = await getDocs(collection(db, 'stats'));
      setStats(statsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })).sort((a, b) => a.order - b.order));
    } catch (error) {
      console.error('❌ Error fetching stats:', error.message);
    }

    // Fetch about
    try {
      const aboutDoc = await getDoc(doc(db, 'settings', 'about'));
      if (aboutDoc.exists()) setAbout(aboutDoc.data());
    } catch (error) {
      console.error('❌ Error fetching about:', error.message);
    }

    // Fetch gallery
    try {
      const gallerySnapshot = await getDocs(collection(db, 'gallery'));
      setGallery(gallerySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })).sort((a, b) => a.order - b.order));
    } catch (error) {
      console.error('❌ Error fetching gallery:', error.message);
    }

    // Fetch YouTube video
    try {
      const videoDoc = await getDoc(doc(db, 'settings', 'youtubeVideo'));
      if (videoDoc.exists()) setYoutubeVideo(videoDoc.data());
    } catch (error) {
      console.error('❌ Error fetching YouTube video:', error.message);
    }

    // Fetch pricing
    try {
      const pricingSnapshot = await getDocs(collection(db, 'pricing'));
      setPricing(pricingSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })).sort((a, b) => a.order - b.order));
    } catch (error) {
      console.error('❌ Error fetching pricing:', error.message);
    }

    // Fetch contact
    try {
      const contactDoc = await getDoc(doc(db, 'settings', 'contact'));
      if (contactDoc.exists()) setContact(contactDoc.data());
    } catch (error) {
      console.error('❌ Error fetching contact:', error.message);
    }

    // Fetch footer
    try {
      const footerDoc = await getDoc(doc(db, 'settings', 'footer'));
      if (footerDoc.exists()) setFooter(footerDoc.data());
    } catch (error) {
      console.error('❌ Error fetching footer:', error.message);
    }

    // Fetch trainers
    try {
      const trainersSnapshot = await getDocs(collection(db, 'trainers'));
      const trainersData = trainersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setTrainers(trainersData);
    } catch (error) {
      console.error('❌ Error fetching trainers:', error.message);
    }

    // Fetch locations
    try {
      const locationsSnapshot = await getDocs(collection(db, 'locations'));
      setLocations(locationsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })).sort((a, b) => a.order - b.order));
    } catch (error) {
      console.error('❌ Error fetching locations:', error.message);
    }

    // Fetch service areas
    try {
      const serviceAreasDoc = await getDoc(doc(db, 'settings', 'serviceAreas'));
      if (serviceAreasDoc.exists()) setServiceAreas(serviceAreasDoc.data());
    } catch (error) {
      console.error('❌ Error fetching service areas:', error.message);
    }

    // Fetch mobile training
    try {
      const mobileTrainingDoc = await getDoc(doc(db, 'settings', 'mobileTraining'));
      if (mobileTrainingDoc.exists()) setMobileTraining(mobileTrainingDoc.data());
    } catch (error) {
      console.error('❌ Error fetching mobile training:', error.message);
    }

    // Fetch reviews
    try {
      const reviewsSnapshot = await getDocs(collection(db, 'reviews'));
      setReviews(reviewsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })).sort((a, b) => (b.order || 0) - (a.order || 0)));
    } catch (error) {
      console.error('❌ Error fetching reviews:', error.message);
    }

    // Fetch Group Page data
    try {
      const groupHeroDoc = await getDoc(doc(db, 'settings', 'groupHero'));
      if (groupHeroDoc.exists()) setGroupHero(groupHeroDoc.data());
      
      const groupVideoDoc = await getDoc(doc(db, 'settings', 'groupVideo'));
      if (groupVideoDoc.exists()) setGroupVideo(groupVideoDoc.data());
      else setGroupVideo({ url: '', title: '' });
      
      const groupWhoForDoc = await getDoc(doc(db, 'settings', 'groupWhoFor'));
      if (groupWhoForDoc.exists()) setGroupWhoFor(groupWhoForDoc.data());
      
      const groupPackagesSnapshot = await getDocs(collection(db, 'groupPackages'));
      const groupPackagesData = groupPackagesSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .sort((a, b) => (a.order || 0) - (b.order || 0));
      setGroupPackages(groupPackagesData);

      const groupScheduleDoc = await getDoc(doc(db, 'settings', 'groupSchedule'));
      if (groupScheduleDoc.exists()) setGroupSchedule(groupScheduleDoc.data());
      
      const groupCTADoc = await getDoc(doc(db, 'settings', 'groupFinalCTA'));
      if (groupCTADoc.exists()) setGroupFinalCTA(groupCTADoc.data());

      const groupGallerySnapshot = await getDocs(collection(db, 'groupGallery'));
      setGroupGallery(groupGallerySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })).sort((a, b) => (a.order || 0) - (b.order || 0)));
    } catch (error) {
      console.error('❌ Error fetching Group Page data:', error.message);
    }

    // Fetch Mobile Page Hero
    try {
      const mobilePageHeroDoc = await getDoc(doc(db, 'settings', 'mobileHero'));
      if (mobilePageHeroDoc.exists()) setMobilePageHero(mobilePageHeroDoc.data());
    } catch (error) {
      console.error('❌ Error fetching mobile hero:', error.message);
    }
      
    // Fetch Mobile Page Video
    try {
      const mobilePageVideoDoc = await getDoc(doc(db, 'settings', 'mobileVideo'));
      if (mobilePageVideoDoc.exists()) setMobilePageVideo(mobilePageVideoDoc.data());
      else setMobilePageVideo({ url: '', title: '' });
    } catch (error) {
      console.error('❌ Error fetching mobile video:', error.message);
    }
      
    // Fetch Mobile Page Who For
    try {
      const mobilePageWhoForDoc = await getDoc(doc(db, 'settings', 'mobileWhoFor'));
      if (mobilePageWhoForDoc.exists()) setMobilePageWhoFor(mobilePageWhoForDoc.data());
    } catch (error) {
      console.error('❌ Error fetching mobile who for:', error.message);
    }
      
    // Fetch Mobile Packages (CRITICAL - isolated try-catch)
    try {
      console.log('🟢 FETCHING MOBILE PACKAGES - START');
      const mobilePackagesSnapshot = await getDocs(collection(db, 'mobilePackages'));
      console.log('Raw snapshot size:', mobilePackagesSnapshot.size);
      console.log('Raw docs:', mobilePackagesSnapshot.docs.map(d => ({ id: d.id, data: d.data() })));
      const mobilePackagesData = mobilePackagesSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .sort((a, b) => (a.order || 0) - (b.order || 0));
      console.log('✅ Mobile Packages fetched successfully:', mobilePackagesData.length, 'packages:', mobilePackagesData);
      console.log('Setting mobilePackages state with:', mobilePackagesData);
      setMobilePackages(mobilePackagesData);
      console.log('✅ State set complete');
    } catch (mobilePackagesError) {
      console.error('❌ Error fetching mobile packages:', mobilePackagesError);
      setMobilePackages([]);
    }
      
    // Fetch Mobile Page CTA
    try {
      const mobilePageCTADoc = await getDoc(doc(db, 'settings', 'mobileFinalCTA'));
      if (mobilePageCTADoc.exists()) setMobilePageFinalCTA(mobilePageCTADoc.data());
    } catch (error) {
      console.error('❌ Error fetching mobile CTA:', error.message);
    }

    // Fetch Mobile Gallery
    try {
      const mobileGallerySnapshot = await getDocs(collection(db, 'mobileGallery'));
      setMobileGallery(mobileGallerySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })).sort((a, b) => (a.order || 0) - (b.order || 0)));
    } catch (error) {
      console.error('❌ Error fetching mobile gallery:', error.message);
    }
      
    console.log('🔴 FETCH ALL DATA - COMPLETED');
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleAnnouncementSave = async () => {
    try {
      const sanitizedData = {
        active: announcement.active,
        message: sanitizeString(announcement.message),
        linkUrl: sanitizeURL(announcement.linkUrl),
        linkText: sanitizeString(announcement.linkText),
        scrollToSection: sanitizeString(announcement.scrollToSection || '', 100),
        updatedAt: serverTimestamp(),
        updatedBy: auth.currentUser?.uid || 'admin'
      };
      
      await setDoc(doc(db, 'settings', 'announcement'), sanitizedData);
      alert('Announcement updated successfully!');
    } catch (error) {
      console.error('Error saving announcement:', error);
      alert(error.message || 'Error saving announcement');
    }
  };

  const handleHeroSave = async () => {
    try {
      const sanitizedData = {
        title: sanitizeString(hero.title),
        subtitle: sanitizeString(hero.subtitle),
        buttonText: sanitizeString(hero.buttonText),
        backgroundVideoUrl: sanitizeURL(hero.backgroundVideoUrl || ''),
        updatedAt: serverTimestamp(),
        updatedBy: auth.currentUser?.uid || 'admin'
      };
      
      await setDoc(doc(db, 'settings', 'hero'), sanitizedData);
      alert('Hero section updated successfully!');
    } catch (error) {
      console.error('Error saving hero:', error);
      alert(error.message || 'Error saving hero section');
    }
  };

  const handleGrowthWayUpdate = (id, field, value) => {
    // Update local state immediately for better UX
    setGrowthWays(growthWays.map(g => g.id === id ? { ...g, [field]: value } : g));
  };

  const handleGrowthWaySave = async (id) => {
    try {
      const item = growthWays.find(g => g.id === id);
      const sanitizedData = {
        title: sanitizeString(item.title),
        description: sanitizeString(item.description),
        features: sanitizeArray(item.features),
        featured: item.featured || false,
        imageUrl: sanitizeURL(item.imageUrl || ''),
        calendlyUrl: sanitizeURL(item.calendlyUrl || ''),
        learnMoreUrl: sanitizeURL(item.learnMoreUrl || ''),
        order: item.order,
        updatedAt: serverTimestamp(),
        updatedBy: auth.currentUser?.uid || 'admin'
      };
      
      await updateDoc(doc(db, 'growthWays', id), sanitizedData);
      alert('Growth way updated successfully!');
    } catch (error) {
      console.error('Error saving growth way:', error);
      alert(error.message || 'Error saving growth way');
    }
  };

  const handleStatsUpdate = (id, field, value) => {
    // Update local state immediately for better UX
    setStats(stats.map(s => s.id === id ? { ...s, [field]: value } : s));
  };

  const handleStatSave = async (id) => {
    try {
      const stat = stats.find(s => s.id === id);
      const sanitizedData = {
        number: sanitizeNumber(stat.number),
        label: sanitizeString(stat.label),
        order: stat.order,
        updatedAt: serverTimestamp(),
        updatedBy: auth.currentUser?.uid || 'admin'
      };
      
      validateStatData(sanitizedData);
      
      await updateDoc(doc(db, 'stats', id), sanitizedData);
      alert('Stat updated successfully!');
    } catch (error) {
      console.error('Error saving stat:', error);
      alert(error.message || 'Error saving stat');
    }
  };

  const handleAddStat = async (e) => {
    e.preventDefault();
    try {
      const sanitizedData = {
        number: sanitizeNumber(newStat.number),
        label: sanitizeString(newStat.label),
        order: stats.length + 1
      };
      
      validateStatData(sanitizedData);
      
      const dataWithMetadata = {
        ...sanitizedData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: auth.currentUser?.uid || 'admin',
        updatedBy: auth.currentUser?.uid || 'admin'
      };
      
      await addDoc(collection(db, 'stats'), dataWithMetadata);
      alert('Stat added successfully!');
      setNewStat({ number: '', label: '' });
      setShowNewStatForm(false);
      fetchAllData();
    } catch (error) {
      console.error('Error adding stat:', error);
      alert(error.message || 'Error adding stat');
    }
  };

  const handleDeleteStat = async (statId) => {
    if (window.confirm('Are you sure you want to delete this statistic?')) {
      try {
        await deleteDoc(doc(db, 'stats', statId));
        alert('Stat deleted successfully!');
        fetchAllData();
      } catch (error) {
        console.error('Error deleting stat:', error);
        alert('Error deleting stat');
      }
    }
  };

  const handleAboutSave = async () => {
    try {
      const certArray = typeof about.certifications === 'string' 
        ? sanitizeArray(about.certifications.split(','))
        : sanitizeArray(about.certifications);
        
      const sanitizedData = {
        title: sanitizeString(about.title),
        content: sanitizeString(about.content),
        certifications: certArray,
        imageUrl: sanitizeURL(about.imageUrl),
        updatedAt: serverTimestamp(),
        updatedBy: auth.currentUser?.uid || 'admin'
      };
      
      await setDoc(doc(db, 'settings', 'about'), sanitizedData);
      alert('About section updated successfully!');
    } catch (error) {
      console.error('Error saving about:', error);
      alert(error.message || 'Error saving about section');
    }
  };

  const handleAddGalleryImage = async (imageUrl) => {
    try {
      const sanitizedData = {
        imageUrl: sanitizeURL(imageUrl),
        order: gallery.length + 1,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: auth.currentUser?.uid || 'admin',
        updatedBy: auth.currentUser?.uid || 'admin'
      };
      
      if (!sanitizedData.imageUrl) {
        throw new Error('Invalid image URL');
      }
      
      await addDoc(collection(db, 'gallery'), sanitizedData);
      alert('Image added to gallery!');
      fetchAllData();
    } catch (error) {
      console.error('Error adding gallery image:', error);
      alert(error.message || 'Error adding image');
    }
  };

  const handleDeleteGalleryImage = async (imageId) => {
    if (window.confirm('Are you sure you want to delete this image from the gallery?')) {
      try {
        await deleteDoc(doc(db, 'gallery', imageId));
        alert('Image deleted from gallery!');
        fetchAllData();
      } catch (error) {
        console.error('Error deleting gallery image:', error);
        alert('Error deleting image');
      }
    }
  };

  const handleYouTubeSave = async () => {
    try {
      const sanitizedData = {
        url: sanitizeYouTubeURL(youtubeVideo.url),
        title: sanitizeString(youtubeVideo.title),
        updatedAt: serverTimestamp(),
        updatedBy: auth.currentUser?.uid || 'admin'
      };
      
      if (sanitizedData.url && !sanitizedData.url.includes('youtube.com') && !sanitizedData.url.includes('youtu.be')) {
        throw new Error('Please enter a valid YouTube URL');
      }
      
      await setDoc(doc(db, 'settings', 'youtubeVideo'), sanitizedData);
      alert('YouTube video updated successfully!');
    } catch (error) {
      console.error('Error saving YouTube video:', error);
      alert(error.message || 'Error saving YouTube video');
    }
  };

  const handlePricingUpdate = (id, field, value) => {
    // Update local state immediately for better UX
    setPricing(pricing.map(p => p.id === id ? { ...p, [field]: value } : p));
  };

  const handlePricingSave = async (id) => {
    try {
      const plan = pricing.find(p => p.id === id);
      const features = typeof plan.features === 'string'
        ? sanitizeArray(plan.features.split('\n'))
        : sanitizeArray(plan.features);
        
      const sanitizedData = {
        title: sanitizeString(plan.title),
        price: sanitizeString(plan.price),
        period: sanitizeString(plan.period),
        features: features,
        calendlyUrl: plan.calendlyUrl || '',
        learnMoreUrl: plan.learnMoreUrl || '',
        featured: plan.featured || false,
        order: plan.order,
        updatedAt: serverTimestamp(),
        updatedBy: auth.currentUser?.uid || 'admin'
      };
      
      await updateDoc(doc(db, 'pricing', id), sanitizedData);
      alert('Pricing plan updated successfully!');
    } catch (error) {
      console.error('Error saving pricing:', error);
      alert(error.message || 'Error saving pricing');
    }
  };

  const handleAddPricing = async (e) => {
    e.preventDefault();
    try {
      const features = newPricing.features.split('\n').filter(f => f.trim());
      
      const sanitizedData = {
        title: sanitizeString(newPricing.title, 100),
        price: sanitizeString(newPricing.price, 20),
        period: sanitizeString(newPricing.period, 20),
        features: sanitizeArray(features),
        featured: !!newPricing.featured,
        order: pricing.length + 1,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: auth.currentUser?.uid || 'admin',
        updatedBy: auth.currentUser?.uid || 'admin'
      };
      
      await addDoc(collection(db, 'pricing'), sanitizedData);
      alert('Pricing plan added successfully!');
      setNewPricing({ title: '', price: '', period: '', features: '', featured: false });
      setShowNewPricingForm(false);
      fetchAllData();
    } catch (error) {
      console.error('Error adding pricing:', error);
      alert(error.message || 'Error adding pricing plan');
    }
  };

  const handleDeletePricing = async (id) => {
    if (window.confirm('Are you sure you want to delete this pricing plan?')) {
      try {
        await deleteDoc(doc(db, 'pricing', id));
        alert('Pricing plan deleted successfully!');
        fetchAllData();
      } catch (error) {
        console.error('Error deleting pricing:', error);
        alert(error.message || 'Error deleting pricing plan');
      }
    }
  };

  const handleContactSave = async () => {
    try {
      const sanitizedData = {
        title: sanitizeString(contact.title),
        subtitle: sanitizeString(contact.subtitle),
        updatedAt: serverTimestamp(),
        updatedBy: auth.currentUser?.uid || 'admin'
      };
      
      await setDoc(doc(db, 'settings', 'contact'), sanitizedData);
      alert('Contact section updated successfully!');
    } catch (error) {
      console.error('Error saving contact:', error);
      alert(error.message || 'Error saving contact section');
    }
  };

  const handleFooterSave = async () => {
    try {
      const sanitizedData = {
        copyright: sanitizeString(footer.copyright),
        instagramUrl: sanitizeURL(footer.instagramUrl),
        tiktokUrl: sanitizeURL(footer.tiktokUrl),
        facebookUrl: sanitizeURL(footer.facebookUrl),
        updatedAt: serverTimestamp(),
        updatedBy: auth.currentUser?.uid || 'admin'
      };
      
      await setDoc(doc(db, 'settings', 'footer'), sanitizedData);
      alert('Footer updated successfully!');
    } catch (error) {
      console.error('Error saving footer:', error);
      alert(error.message || 'Error saving footer');
    }
  };

  // Group Package Handlers
  const handleAddGroupPackage = async (e) => {
    e.preventDefault();
    try {
      const features = newGroupPackage.features.split('\n').filter(f => f.trim());
      const sanitizedData = {
        title: sanitizeString(newGroupPackage.title, 100),
        price: sanitizeString(newGroupPackage.price, 50),
        description: sanitizeString(newGroupPackage.description, 500),
        features: sanitizeArray(features),
        calendlyUrl: sanitizeURL(newGroupPackage.calendlyUrl),
        order: sanitizeNumber(newGroupPackage.order),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      await addDoc(collection(db, 'groupPackages'), sanitizedData);
      alert('Package added successfully!');
      setNewGroupPackage({ title: '', price: '', description: '', features: '', calendlyUrl: '', order: 0 });
      setShowNewGroupPackageForm(false);
      fetchAllData();
    } catch (error) {
      alert(error.message || 'Error adding package');
    }
  };

  const handleDeleteGroupPackage = async (id) => {
    if (window.confirm('Are you sure you want to delete this package?')) {
      try {
        await deleteDoc(doc(db, 'groupPackages', id));
        alert('Package deleted successfully!');
        fetchAllData();
      } catch (error) {
        alert(error.message || 'Error deleting package');
      }
    }
  };

  const handleGroupPackageUpdate = (id, field, value) => {
    setGroupPackages(groupPackages.map(pkg => 
      pkg.id === id ? { ...pkg, [field]: value } : pkg
    ));
  };

  const handleGroupPackageSave = async (id) => {
    try {
      const pkg = groupPackages.find(p => p.id === id);
      if (!pkg) return;
      const features = typeof pkg.features === 'string' ? pkg.features.split('\n').filter(f => f.trim()) : pkg.features;
      const sanitizedData = {
        title: sanitizeString(pkg.title, 100),
        price: sanitizeString(pkg.price, 50),
        description: sanitizeString(pkg.description, 500),
        features: sanitizeArray(features),
        calendlyUrl: sanitizeURL(pkg.calendlyUrl),
        order: sanitizeNumber(pkg.order),
        updatedAt: serverTimestamp()
      };
      await updateDoc(doc(db, 'groupPackages', id), sanitizedData);
      alert('Package updated successfully!');
      fetchAllData();
    } catch (error) {
      alert(error.message || 'Error updating package');
    }
  };

  // Mobile Package Handlers
  const handleAddMobilePackage = async (e) => {
    e.preventDefault();
    console.log('🔵 ADDING MOBILE PACKAGE - START');
    console.log('Form data:', newMobilePackage);
    try {
      const features = newMobilePackage.features.split('\n').filter(f => f.trim());
      const sanitizedData = {
        title: sanitizeString(newMobilePackage.title, 100),
        price: sanitizeString(newMobilePackage.price, 50),
        description: sanitizeString(newMobilePackage.description, 500),
        features: sanitizeArray(features),
        calendlyUrl: sanitizeURL(newMobilePackage.calendlyUrl),
        order: sanitizeNumber(newMobilePackage.order),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      console.log('Sanitized data to save:', sanitizedData);
      const docRef = await addDoc(collection(db, 'mobilePackages'), sanitizedData);
      console.log('✅ Package added successfully with ID:', docRef.id);
      alert('Package added successfully!');
      setNewMobilePackage({ title: '', price: '', description: '', features: '', calendlyUrl: '', order: 0 });
      setShowNewMobilePackageForm(false);
      console.log('🔵 Calling fetchAllData to refresh...');
      await fetchAllData();
      console.log('✅ fetchAllData completed');
    } catch (error) {
      console.error('❌ Error adding mobile package:', error);
      alert(error.message || 'Error adding package');
    }
  };

  const handleDeleteMobilePackage = async (id) => {
    if (window.confirm('Are you sure you want to delete this package?')) {
      try {
        await deleteDoc(doc(db, 'mobilePackages', id));
        alert('Package deleted successfully!');
        fetchAllData();
      } catch (error) {
        alert(error.message || 'Error deleting package');
      }
    }
  };

  const handleMobilePackageUpdate = (id, field, value) => {
    setMobilePackages(mobilePackages.map(pkg => 
      pkg.id === id ? { ...pkg, [field]: value } : pkg
    ));
  };

  const handleMobilePackageSave = async (id) => {
    try {
      const pkg = mobilePackages.find(p => p.id === id);
      if (!pkg) return;
      const features = typeof pkg.features === 'string' ? pkg.features.split('\n').filter(f => f.trim()) : pkg.features;
      const sanitizedData = {
        title: sanitizeString(pkg.title, 100),
        price: sanitizeString(pkg.price, 50),
        description: sanitizeString(pkg.description, 500),
        features: sanitizeArray(features),
        calendlyUrl: sanitizeURL(pkg.calendlyUrl),
        order: sanitizeNumber(pkg.order),
        updatedAt: serverTimestamp()
      };
      await updateDoc(doc(db, 'mobilePackages', id), sanitizedData);
      alert('Package updated successfully!');
      fetchAllData();
    } catch (error) {
      alert(error.message || 'Error updating package');
    }
  };

  // Location handlers
  const handleLocationUpdate = (id, field, value) => {
    setLocations(locations.map(l => l.id === id ? { ...l, [field]: value } : l));
  };

  const handleLocationSave = async (id) => {
    try {
      const location = locations.find(l => l.id === id);
      const features = typeof location.features === 'string'
        ? location.features.split('\n').filter(f => f.trim())
        : (Array.isArray(location.features) ? location.features : []);
      
      const sanitizedData = {
        name: location.name || '',
        address: location.address || '',
        description: location.description || '',
        hours: location.hours || '',
        features: features,
        mapLink: location.mapLink || '',
        featured: !!location.featured,
        order: location.order || 0,
        updatedAt: serverTimestamp(),
        updatedBy: auth.currentUser?.uid || 'admin'
      };
      
      await updateDoc(doc(db, 'locations', id), sanitizedData);
      alert('Location updated successfully!');
    } catch (error) {
      console.error('Error saving location:', error);
      alert(error.message || 'Error saving location');
    }
  };

  const handleAddLocation = async (e) => {
    e.preventDefault();
    try {
      const features = newLocation.features.split('\n').filter(f => f.trim());
      
      const sanitizedData = {
        name: newLocation.name || '',
        address: newLocation.address || '',
        description: newLocation.description || '',
        hours: newLocation.hours || '',
        features: features,
        mapLink: newLocation.mapLink || '',
        featured: !!newLocation.featured,
        order: locations.length + 1,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: auth.currentUser?.uid || 'admin',
        updatedBy: auth.currentUser?.uid || 'admin'
      };
      
      await addDoc(collection(db, 'locations'), sanitizedData);
      alert('Location added successfully!');
      setNewLocation({ name: '', address: '', description: '', hours: '', features: '', mapLink: '', featured: false });
      setShowNewLocationForm(false);
      fetchAllData();
    } catch (error) {
      console.error('Error adding location:', error);
      alert(error.message || 'Error adding location');
    }
  };

  const handleDeleteLocation = async (id) => {
    if (window.confirm('Are you sure you want to delete this location?')) {
      try {
        await deleteDoc(doc(db, 'locations', id));
        alert('Location deleted successfully!');
        fetchAllData();
      } catch (error) {
        console.error('Error deleting location:', error);
        alert(error.message || 'Error deleting location');
      }
    }
  };

  const handleServiceAreasSave = async () => {
    try {
      const sanitizedData = {
        title: serviceAreas?.title || '',
        description: serviceAreas?.description || '',
        onlineNotice: serviceAreas?.onlineNotice || '',
        updatedAt: serverTimestamp(),
        updatedBy: auth.currentUser?.uid || 'admin'
      };
      
      await setDoc(doc(db, 'settings', 'serviceAreas'), sanitizedData);
      alert('Service areas updated successfully!');
    } catch (error) {
      console.error('Error saving service areas:', error);
      alert(error.message || 'Error saving service areas');
    }
  };

  const handleMobileTrainingSave = async () => {
    try {
      const features = typeof mobileTraining.features === 'string'
        ? mobileTraining.features.split('\n').filter(f => f.trim())
        : (Array.isArray(mobileTraining.features) ? mobileTraining.features : []);
      
      const sanitizedData = {
        title: mobileTraining?.title || '',
        subtitle: mobileTraining?.subtitle || '',
        description: mobileTraining?.description || '',
        imageUrl: mobileTraining?.imageUrl || '',
        features: features,
        updatedAt: serverTimestamp(),
        updatedBy: auth.currentUser?.uid || 'admin'
      };
      
      await setDoc(doc(db, 'settings', 'mobileTraining'), sanitizedData);
      alert('Mobile training section updated successfully!');
    } catch (error) {
      console.error('Error saving mobile training:', error);
      alert(error.message || 'Error saving mobile training');
    }
  };

  // Reviews handlers
  const handleAddReview = async () => {
    try {
      const newReview = {
        name: '',
        text: '',
        rating: 5,
        achievement: '',
        imageUrl: '',
        active: true,
        order: reviews.length,
        createdAt: serverTimestamp(),
        createdBy: auth.currentUser?.uid || 'admin'
      };
      
      const docRef = await addDoc(collection(db, 'reviews'), newReview);
      setReviews([...reviews, { id: docRef.id, ...newReview }]);
      alert('New review added! Fill in the details below.');
    } catch (error) {
      console.error('Error adding review:', error);
      alert(error.message || 'Error adding review');
    }
  };

  const handleReviewUpdate = (id, field, value) => {
    setReviews(reviews.map(review => 
      review.id === id ? { ...review, [field]: value } : review
    ));
  };

  const handleReviewSave = async (reviewId) => {
    try {
      const review = reviews.find(r => r.id === reviewId);
      if (!review) return;

      const sanitizedData = {
        name: review.name || '',
        text: review.text || '',
        rating: Math.min(5, Math.max(1, parseInt(review.rating) || 5)),
        achievement: review.achievement || '',
        imageUrl: review.imageUrl || '',
        active: review.active !== false,
        order: review.order || 0,
        updatedAt: serverTimestamp(),
        updatedBy: auth.currentUser?.uid || 'admin'
      };
      
      await setDoc(doc(db, 'reviews', reviewId), sanitizedData, { merge: true });
      alert('Review saved successfully!');
    } catch (error) {
      console.error('Error saving review:', error);
      alert(error.message || 'Error saving review');
    }
  };

  const handleDeleteReview = async (reviewId) => {
    if (window.confirm('Are you sure you want to delete this review?')) {
      try {
        await deleteDoc(doc(db, 'reviews', reviewId));
        setReviews(reviews.filter(review => review.id !== reviewId));
        alert('Review deleted successfully!');
      } catch (error) {
        console.error('Error deleting review:', error);
        alert(error.message || 'Error deleting review');
      }
    }
  };

  const handleServiceUpdate = (serviceId, field, value) => {
    // Update local state immediately for better UX
    setServices(services.map(s => s.id === serviceId ? { ...s, [field]: value } : s));
  };

  const handleServiceSave = async (serviceId) => {
    try {
      const service = services.find(s => s.id === serviceId);
      const sanitizedData = {
        title: sanitizeString(service.title),
        description: sanitizeString(service.description),
        order: service.order,
        updatedAt: serverTimestamp(),
        updatedBy: auth.currentUser?.uid || 'admin'
      };
      
      validateServiceData(sanitizedData);
      
      await updateDoc(doc(db, 'services', serviceId), sanitizedData);
      alert('Service updated successfully!');
    } catch (error) {
      console.error('Error saving service:', error);
      alert(error.message || 'Error saving service');
    }
  };

  const handleAddService = async (e) => {
    e.preventDefault();
    try {
      const sanitizedData = {
        title: sanitizeString(newService.title),
        description: sanitizeString(newService.description),
        order: services.length + 1
      };
      
      validateServiceData(sanitizedData);
      
      const dataWithMetadata = {
        ...sanitizedData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: auth.currentUser?.uid || 'admin',
        updatedBy: auth.currentUser?.uid || 'admin'
      };
      
      await addDoc(collection(db, 'services'), dataWithMetadata);
      alert('Service added successfully!');
      setNewService({ title: '', description: '' });
      setShowNewServiceForm(false);
      fetchAllData();
    } catch (error) {
      console.error('Error adding service:', error);
      alert(error.message || 'Error adding service');
    }
  };

  const handleDeleteService = async (serviceId) => {
    if (window.confirm('Are you sure you want to delete this service?')) {
      try {
        await deleteDoc(doc(db, 'services', serviceId));
        alert('Service deleted successfully!');
        fetchAllData();
      } catch (error) {
        console.error('Error deleting service:', error);
        alert('Error deleting service');
      }
    }
  };

  const handleTrainerSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const sanitizedData = {
        name: sanitizeString(trainerForm.name),
        specialty: sanitizeString(trainerForm.specialty),
        bio: sanitizeString(trainerForm.bio),
        experience: sanitizeString(trainerForm.experience),
        certifications: sanitizeArray(trainerForm.certifications.split(',')),
        imageUrl: sanitizeURL(trainerForm.imageUrl),
        calendlyUrl: sanitizeURL(trainerForm.calendlyUrl)
      };

      validateTrainerData(sanitizedData);

      if (editingTrainerId) {
        // Update existing trainer
        const dataWithMetadata = {
          ...sanitizedData,
          updatedAt: serverTimestamp(),
          updatedBy: auth.currentUser?.uid || 'admin'
        };
        await updateDoc(doc(db, 'trainers', editingTrainerId), dataWithMetadata);
        alert('Trainer updated successfully!');
      } else {
        // Add new trainer
        const dataWithMetadata = {
          ...sanitizedData,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          createdBy: auth.currentUser?.uid || 'admin',
          updatedBy: auth.currentUser?.uid || 'admin'
        };
        await addDoc(collection(db, 'trainers'), dataWithMetadata);
        alert('Trainer added successfully!');
      }

      // Reset form and refresh trainers
      setTrainerForm({
        name: '',
        specialty: '',
        bio: '',
        experience: '',
        certifications: '',
        imageUrl: '',
        calendlyUrl: ''
      });
      setEditingTrainerId(null);
      
      // Immediately refresh trainers list
      const trainersSnapshot = await getDocs(collection(db, 'trainers'));
      const trainersData = trainersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setTrainers(trainersData);
    } catch (error) {
      console.error('Error saving trainer:', error);
      alert(error.message || 'Error saving trainer. Please try again.');
    }
  };

  const handleEditTrainer = (trainer) => {
    setTrainerForm({
      name: trainer.name || '',
      specialty: trainer.specialty || '',
      bio: trainer.bio || '',
      experience: trainer.experience || '',
      certifications: trainer.certifications?.join(', ') || '',
      imageUrl: trainer.imageUrl || '',
      calendlyUrl: trainer.calendlyUrl || ''
    });
    setEditingTrainerId(trainer.id);
  };

  const handleDeleteTrainer = async (id) => {
    if (window.confirm('Are you sure you want to delete this trainer?')) {
      try {
        await deleteDoc(doc(db, 'trainers', id));
        alert('Trainer deleted successfully!');
        fetchAllData();
      } catch (error) {
        console.error('Error deleting trainer:', error);
        alert('Error deleting trainer');
      }
    }
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-header">
        <div className="admin-header-content">
          <h1 className="admin-title">Admin Dashboard</h1>
          <button onClick={handleLogout} className="btn-logout">
            <LogOut size={20} />
            <span>Logout</span>
          </button>
        </div>
      </div>

      <div className="admin-container">
        <div className="admin-tabs">
          <button className={`tab-button ${activeTab === 'main' ? 'active' : ''}`} onClick={() => { setActiveTab('main'); setActiveSection(null); }}>Main Page</button>
          <button className={`tab-button ${activeTab === 'mobile-page' ? 'active' : ''}`} onClick={() => { setActiveTab('mobile-page'); setActiveMobileSection(null); }}>Mobile Page</button>
          <button className={`tab-button ${activeTab === 'group-page' ? 'active' : ''}`} onClick={() => { setActiveTab('group-page'); setActiveGroupSection(null); }}>Group Page</button>
        </div>

        {/* Main Page - Section Grid */}
        {activeTab === 'main' && !activeSection && (
          <div className="admin-section">
            <h2 className="section-title-admin">Main Page Sections</h2>
            <div className="main-sections-grid">
              <div className="section-card" onClick={() => setActiveSection('announcement')}>
                <h3>Announcement</h3>
                <p>Site-wide announcement banner</p>
              </div>
              <div className="section-card" onClick={() => setActiveSection('hero')}>
                <h3>Hero Section</h3>
                <p>Main page header and CTA</p>
              </div>
              <div className="section-card" onClick={() => setActiveSection('growth')}>
                <h3>3 Ways to Grow</h3>
                <p>Training options showcase</p>
              </div>
              <div className="section-card" onClick={() => setActiveSection('youtube')}>
                <h3>YouTube Video</h3>
                <p>Featured video section</p>
              </div>
              <div className="section-card" onClick={() => setActiveSection('trainers')}>
                <h3>Trainers</h3>
                <p>Team member profiles</p>
              </div>
              <div className="section-card" onClick={() => setActiveSection('reviews')}>
                <h3>Reviews</h3>
                <p>Client testimonials</p>
              </div>
              <div className="section-card" onClick={() => setActiveSection('about')}>
                <h3>About Section</h3>
                <p>Meet the trainer</p>
              </div>
              <div className="section-card" onClick={() => setActiveSection('gallery')}>
                <h3>Gallery</h3>
                <p>Image collection</p>
              </div>
              <div className="section-card" onClick={() => setActiveSection('mobile')}>
                <h3>Mobile Training</h3>
                <p>Mobile service section</p>
              </div>
              <div className="section-card" onClick={() => setActiveSection('locations')}>
                <h3>Locations</h3>
                <p>Training locations & areas</p>
              </div>
              <div className="section-card" onClick={() => setActiveSection('pricing')}>
                <h3>Pricing Plans</h3>
                <p>Service packages</p>
              </div>
              <div className="section-card" onClick={() => setActiveSection('services')}>
                <h3>Services</h3>
                <p>What we offer</p>
              </div>
              <div className="section-card" onClick={() => setActiveSection('stats')}>
                <h3>Statistics</h3>
                <p>Key metrics display</p>
              </div>
              <div className="section-card" onClick={() => setActiveSection('contact')}>
                <h3>Contact Section</h3>
                <p>Get in touch form</p>
              </div>
              <div className="section-card" onClick={() => setActiveSection('footer')}>
                <h3>Footer</h3>
                <p>Bottom page information</p>
              </div>
            </div>
            <button onClick={() => setActiveTab('main')} className="btn-submit" style={{marginTop: '1rem'}}>
              ← Back to Main Sections
            </button>
          </div>
        )}

        {/* Announcement Section */}
        {activeTab === 'main' && activeSection === 'announcement' && (
          <div className="admin-section">
            <div style={{display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '2rem'}}>
              <ArrowLeft 
                size={32} 
                style={{cursor: 'pointer', color: 'var(--primary)'}} 
                onClick={() => setActiveSection(null)}
              />
              <h2 className="section-title-admin" style={{marginBottom: 0}}>Site Announcement</h2>
            </div>
            <div className="admin-card">
              <div className="form-group">
                <label className="form-label">
                  <input
                    type="checkbox"
                    checked={announcement.active}
                    onChange={(e) => setAnnouncement({ ...announcement, active: e.target.checked })}
                    style={{marginRight: '0.5rem'}}
                  />
                  Display Announcement
                </label>
              </div>
              <div className="form-group">
                <label htmlFor="announcement-message" className="form-label">Message</label>
                <textarea
                  id="announcement-message"
                  value={announcement.message}
                  onChange={(e) => setAnnouncement({ ...announcement, message: e.target.value })}
                  className="form-textarea"
                  rows="3"
                  placeholder="Enter your announcement message..."
                ></textarea>
              </div>
              <div className="form-group">
                <label htmlFor="announcement-link-text" className="form-label">Link Text (Optional)</label>
                <input
                  type="text"
                  id="announcement-link-text"
                  value={announcement.linkText}
                  onChange={(e) => setAnnouncement({ ...announcement, linkText: e.target.value })}
                  className="form-input"
                  placeholder="e.g., Learn More, Sign Up Now"
                />
              </div>
              <div className="form-group">
                <label htmlFor="announcement-link-url" className="form-label">External Link URL (Optional)</label>
                <input
                  type="url"
                  id="announcement-link-url"
                  value={announcement.linkUrl}
                  onChange={(e) => setAnnouncement({ ...announcement, linkUrl: e.target.value })}
                  className="form-input"
                  placeholder="https://example.com"
                />
                <small style={{display: 'block', marginTop: '0.5rem', color: 'rgba(245, 245, 245, 0.6)'}}>
                  External link (opens in new tab)
                </small>
              </div>
              <div className="form-group">
                <label htmlFor="announcement-scroll" className="form-label">OR Scroll to Section (Optional)</label>
                <p style={{ fontSize: '0.85rem', color: 'rgba(245,245,245,0.55)', marginBottom: '0.5rem' }}>
                  Use <code style={{ color: 'var(--primary)' }}>training-modal</code> to open the training method picker (e.g. free trial banner).
                </p>
                <input
                  type="text"
                  id="announcement-scroll"
                  value={announcement.scrollToSection || ''}
                  onChange={(e) => setAnnouncement({ ...announcement, scrollToSection: e.target.value })}
                  className="form-input"
                  placeholder="e.g., growth-section, pricing, contact"
                />
                <small style={{display: 'block', marginTop: '0.5rem', color: 'rgba(245, 245, 245, 0.6)'}}>
                  Instead of external link, scroll to a section on your page (e.g., growth-section, pricing, about, contact)
                </small>
              </div>
              <button onClick={handleAnnouncementSave} className="btn-submit">
                Save Announcement
              </button>
              <button onClick={() => setActiveSection(null)} className="btn-submit-small" style={{marginLeft: '1rem', background: 'rgba(255, 77, 0, 0.2)', border: '2px solid var(--primary)'}}>
                ← Back to Sections
              </button>
            </div>
          </div>
        )}

        {/* Hero Section */}
        {activeTab === 'main' && activeSection === 'hero' && (
          <div className="admin-section">
            <div style={{display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '2rem'}}>
              <ArrowLeft 
                size={32} 
                style={{cursor: 'pointer', color: 'var(--primary)'}} 
                onClick={() => setActiveSection(null)}
              />
              <h2 className="section-title-admin" style={{marginBottom: 0}}>Hero Section</h2>
            </div>
            <div className="admin-card">
              <div className="form-group">
                <label htmlFor="hero-title" className="form-label">Main Title</label>
                <input
                  type="text"
                  id="hero-title"
                  value={hero.title}
                  onChange={(e) => setHero({ ...hero, title: e.target.value })}
                  className="form-input"
                  placeholder="TRANSFORM"
                />
              </div>
              <div className="form-group">
                <label htmlFor="hero-subtitle" className="form-label">Subtitle</label>
                <input
                  type="text"
                  id="hero-subtitle"
                  value={hero.subtitle}
                  onChange={(e) => setHero({ ...hero, subtitle: e.target.value })}
                  className="form-input"
                  placeholder="Elite Personal Training"
                />
              </div>
              <div className="form-group">
                <label htmlFor="hero-button" className="form-label">Button Text</label>
                <input
                  type="text"
                  id="hero-button"
                  value={hero.buttonText}
                  onChange={(e) => setHero({ ...hero, buttonText: e.target.value })}
                  className="form-input"
                  placeholder="Start Your Journey"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Hero background video (MP4)</label>
                <p style={{ marginBottom: '0.75rem', fontSize: '0.85rem', color: 'rgba(245,245,245,0.65)' }}>
                  Upload an MP4 or paste a URL below. Leave both empty for a solid black hero.
                </p>
                <VideoUpload
                  folder="hero-background"
                  currentVideoUrl={hero.backgroundVideoUrl || ''}
                  onUploadComplete={(url) => setHero({ ...hero, backgroundVideoUrl: url })}
                />
                <label htmlFor="hero-bg-video" className="form-label" style={{ marginTop: '1rem' }}>Video URL (optional override)</label>
                <input
                  type="url"
                  id="hero-bg-video"
                  value={hero.backgroundVideoUrl || ''}
                  onChange={(e) => setHero({ ...hero, backgroundVideoUrl: e.target.value })}
                  className="form-input"
                  placeholder="https://… (direct MP4 link if not uploading)"
                />
              </div>
              <button onClick={handleHeroSave} className="btn-submit">
                Save Hero Section
              </button>
              <button onClick={() => setActiveSection(null)} className="btn-submit-small" style={{marginLeft: '1rem', background: 'rgba(255, 77, 0, 0.2)', border: '2px solid var(--primary)'}}>
                ← Back to Sections
              </button>
            </div>
          </div>
        )}

        {/* Growth Ways Section */}
        {activeTab === 'main' && activeSection === 'growth' && (
          <div className="admin-section">
            <div style={{display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '2rem'}}>
              <ArrowLeft 
                size={32} 
                style={{cursor: 'pointer', color: 'var(--primary)'}} 
                onClick={() => setActiveSection(null)}
              />
              <h2 className="section-title-admin" style={{marginBottom: 0}}>3 Ways to Grow</h2>
            </div>
            {growthWays.length === 0 ? (
              <div className="admin-card">
                <p style={{marginBottom: '1rem', color: 'rgba(245, 245, 245, 0.7)'}}>
                  No growth ways found. These are typically set up once and edited as needed. Contact support if you need to add more.
                </p>
              </div>
            ) : (
              <div className="services-admin-grid">
                {growthWays.map((item) => (
                  <div key={item.id} className="admin-card">
                    <h3 className="card-title">{item.title}</h3>
                    <div className="form-group">
                      <label className="form-label">Title</label>
                      <input
                        type="text"
                        value={item.title}
                        onChange={(e) => handleGrowthWayUpdate(item.id, 'title', e.target.value)}
                        className="form-input"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Card image</label>
                      <p style={{fontSize: '0.85rem', color: 'rgba(245, 245, 245, 0.65)', marginBottom: '0.75rem'}}>
                        Shown on the home page “3 Ways to Grow” card. If empty, a default icon is used.
                      </p>
                      <ImageUpload
                        folder="growthWays"
                        currentImageUrl={item.imageUrl || ''}
                        onUploadComplete={(url) => handleGrowthWayUpdate(item.id, 'imageUrl', url)}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Description</label>
                      <textarea
                        value={item.description}
                        onChange={(e) => handleGrowthWayUpdate(item.id, 'description', e.target.value)}
                        className="form-textarea"
                        rows="4"
                      ></textarea>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Features (comma separated)</label>
                      <input
                        type="text"
                        value={Array.isArray(item.features) ? item.features.join(', ') : item.features}
                        onChange={(e) => handleGrowthWayUpdate(item.id, 'features', e.target.value.split(',').map(f => f.trim()))}
                        className="form-input"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">
                        <input
                          type="checkbox"
                          checked={item.featured || false}
                          onChange={(e) => handleGrowthWayUpdate(item.id, 'featured', e.target.checked)}
                          style={{marginRight: '0.5rem'}}
                        />
                        Featured (Shows "Most Popular" badge)
                      </label>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Calendly Booking URL</label>
                      <input
                        type="url"
                        value={item.calendlyUrl || ''}
                        onChange={(e) => handleGrowthWayUpdate(item.id, 'calendlyUrl', e.target.value)}
                        className="form-input"
                        placeholder="https://calendly.com/your-username/method-name"
                      />
                      <small style={{display: 'block', marginTop: '0.5rem', color: 'rgba(245, 245, 245, 0.6)'}}>
                        When users click "Start Your Journey" and select this method, they'll be directed to this Calendly link
                      </small>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Learn More URL (Optional)</label>
                      <input
                        type="text"
                        value={item.learnMoreUrl || ''}
                        onChange={(e) => handleGrowthWayUpdate(item.id, 'learnMoreUrl', e.target.value)}
                        className="form-input"
                        placeholder="/group or /mobile"
                      />
                      <small style={{display: 'block', marginTop: '0.5rem', color: 'rgba(245, 245, 245, 0.6)'}}>
                        Link for "Learn More" button. Use /group or /mobile to link to landing pages.
                      </small>
                    </div>
                    <button onClick={() => handleGrowthWaySave(item.id)} className="btn-submit-small">
                      Save Changes
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Stats Section */}
        {activeTab === 'main' && activeSection === 'stats' && (
          <div className="admin-section">
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem'}}>
              <div style={{display: 'flex', alignItems: 'center', gap: '1rem'}}>
                <ArrowLeft 
                  size={32} 
                  style={{cursor: 'pointer', color: 'var(--primary)'}} 
                  onClick={() => setActiveSection(null)}
                />
                <h2 className="section-title-admin" style={{marginBottom: 0}}>Statistics</h2>
              </div>
              <button 
                onClick={() => setShowNewStatForm(!showNewStatForm)} 
                className="btn-submit-small"
                style={{marginLeft: 'auto'}}
              >
                {showNewStatForm ? 'Cancel' : '+ Add New Stat'}
              </button>
            </div>

            {showNewStatForm && (
              <div className="admin-card" style={{marginBottom: '2rem'}}>
                <h3 className="card-title">Add New Statistic</h3>
                <form onSubmit={handleAddStat}>
                  <div className="form-group">
                    <label className="form-label">Number *</label>
                    <input
                      type="text"
                      value={newStat.number}
                      onChange={(e) => setNewStat({...newStat, number: e.target.value})}
                      className="form-input"
                      required
                      placeholder="e.g., 500+, 10+, 98%"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Label *</label>
                    <input
                      type="text"
                      value={newStat.label}
                      onChange={(e) => setNewStat({...newStat, label: e.target.value})}
                      className="form-input"
                      required
                      placeholder="e.g., Clients Transformed"
                    />
                  </div>
                  <button type="submit" className="btn-submit">Add Statistic</button>
                </form>
              </div>
            )}

            {stats.length === 0 && !showNewStatForm ? (
              <div className="admin-card">
                <p style={{marginBottom: '1rem', color: 'rgba(245, 245, 245, 0.7)'}}>
                  No statistics found. Click "Add New Stat" to create your first statistic.
                </p>
              </div>
            ) : (
              <div className="services-admin-grid">
                {stats.map((stat) => (
                  <div key={stat.id} className="admin-card">
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem'}}>
                      <h3 className="card-title" style={{marginBottom: 0}}>{stat.label}</h3>
                      <button 
                        onClick={() => handleDeleteStat(stat.id)}
                        className="btn-delete"
                        style={{padding: '0.5rem 1rem', fontSize: '0.85rem'}}
                      >
                        Delete
                      </button>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Number</label>
                      <input
                        type="text"
                        value={stat.number}
                        onChange={(e) => handleStatsUpdate(stat.id, 'number', e.target.value)}
                        className="form-input"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Label</label>
                      <input
                        type="text"
                        value={stat.label}
                        onChange={(e) => handleStatsUpdate(stat.id, 'label', e.target.value)}
                        className="form-input"
                      />
                    </div>
                    <button onClick={() => handleStatSave(stat.id)} className="btn-submit-small">
                      Save Changes
                    </button>
                  </div>
              ))}
            </div>
          )}
            <button onClick={() => setActiveSection(null)} className="btn-submit" style={{marginTop: '2rem'}}>
              ← Back to Sections
            </button>
          </div>
        )}

        {/* About Section */}
        {activeTab === 'main' && activeSection === 'about' && (
          <div className="admin-section">
            <div style={{display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '2rem'}}>
              <ArrowLeft 
                size={32} 
                style={{cursor: 'pointer', color: 'var(--primary)'}} 
                onClick={() => setActiveSection(null)}
              />
              <h2 className="section-title-admin" style={{marginBottom: 0}}>About Section</h2>
            </div>
            <div className="admin-card">
              <div className="form-group">
                <label htmlFor="about-title" className="form-label">Title</label>
                <input
                  type="text"
                  id="about-title"
                  value={about.title}
                  onChange={(e) => setAbout({ ...about, title: e.target.value })}
                  className="form-input"
                  placeholder="Meet Carlo"
                />
              </div>
              <div className="form-group">
                <label className="form-label">About Image</label>
                <ImageUpload
                  currentImageUrl={about.imageUrl}
                  onUploadComplete={(url) => setAbout({ ...about, imageUrl: url })}
                  folder="about"
                />
              </div>
              <div className="form-group">
                <label htmlFor="about-content" className="form-label">Content</label>
                <textarea
                  id="about-content"
                  value={about.content}
                  onChange={(e) => setAbout({ ...about, content: e.target.value })}
                  className="form-textarea"
                  rows="8"
                  placeholder="Enter about content..."
                ></textarea>
              </div>
              <div className="form-group">
                <label htmlFor="about-certs" className="form-label">Certifications (comma separated)</label>
                <input
                  type="text"
                  id="about-certs"
                  value={Array.isArray(about.certifications) ? about.certifications.join(', ') : about.certifications}
                  onChange={(e) => setAbout({ ...about, certifications: e.target.value })}
                  className="form-input"
                  placeholder="NASM-CPT, Nutrition Specialist"
                />
              </div>
              <button onClick={handleAboutSave} className="btn-submit">
                Save About Section
              </button>
              <button onClick={() => setActiveSection(null)} className="btn-submit-small" style={{marginLeft: '1rem', background: 'rgba(255, 77, 0, 0.2)', border: '2px solid var(--primary)'}}>
                ← Back to Sections
              </button>
            </div>
          </div>
        )}

        {/* Gallery Section */}
        {activeTab === 'main' && activeSection === 'gallery' && (
          <div className="admin-section">
            <div style={{display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '2rem'}}>
              <ArrowLeft 
                size={32} 
                style={{cursor: 'pointer', color: 'var(--primary)'}} 
                onClick={() => setActiveSection(null)}
              />
              <h2 className="section-title-admin" style={{marginBottom: 0}}>Gallery Images</h2>
            </div>
            <div className="admin-card" style={{marginBottom: '2rem'}}>
              <h3 className="card-title">Add New Image</h3>
              <ImageUpload
                onUploadComplete={handleAddGalleryImage}
                folder="gallery"
              />
            </div>

            {gallery.length === 0 ? (
              <div className="admin-card">
                <p style={{color: 'rgba(245, 245, 245, 0.7)'}}>
                  No images in gallery. Upload images above to add to your gallery.
                </p>
              </div>
            ) : (
              <div className="gallery-grid">
                {gallery.map((image) => (
                  <div key={image.id} className="gallery-item">
                    <img src={image.imageUrl} alt="Gallery" className="gallery-thumbnail" />
                    <button
                      onClick={() => handleDeleteGalleryImage(image.id)}
                      className="btn-delete"
                      style={{marginTop: '0.5rem', width: '100%'}}
                    >
                      Delete
                    </button>
                  </div>
                ))}
              </div>
            )}
            <button onClick={() => setActiveSection(null)} className="btn-submit" style={{marginTop: '2rem'}}>
              ← Back to Sections
            </button>
          </div>
        )}

        {/* YouTube Section */}
        {activeTab === 'main' && activeSection === 'youtube' && (
          <div className="admin-section">
            <div style={{display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '2rem'}}>
              <ArrowLeft 
                size={32} 
                style={{cursor: 'pointer', color: 'var(--primary)'}} 
                onClick={() => setActiveSection(null)}
              />
              <h2 className="section-title-admin" style={{marginBottom: 0}}>YouTube Video</h2>
            </div>
            <div className="admin-card">
              <div className="form-group">
                <label htmlFor="youtube-url" className="form-label">YouTube Video URL</label>
                <input
                  type="text"
                  id="youtube-url"
                  value={youtubeVideo.url}
                  onChange={(e) => setYoutubeVideo({ ...youtubeVideo, url: e.target.value })}
                  className="form-input"
                  placeholder="https://www.youtube.com/watch?v=..."
                />
                <small style={{display: 'block', marginTop: '0.5rem', color: 'rgba(245, 245, 245, 0.6)'}}>
                  Paste the full YouTube video URL
                </small>
              </div>
              <div className="form-group">
                <label htmlFor="youtube-title" className="form-label">Video Title (Optional)</label>
                <input
                  type="text"
                  id="youtube-title"
                  value={youtubeVideo.title}
                  onChange={(e) => setYoutubeVideo({ ...youtubeVideo, title: e.target.value })}
                  className="form-input"
                  placeholder="e.g., Watch Our Success Stories"
                />
              </div>
              {youtubeVideo.url && (
                <div style={{marginBottom: '1.5rem'}}>
                  <p style={{marginBottom: '0.5rem', color: 'rgba(245, 245, 245, 0.7)'}}>Preview:</p>
                  <div style={{position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden'}}>
                    <iframe
                      src={`https://www.youtube.com/embed/${youtubeVideo.url.includes('v=') ? youtubeVideo.url.split('v=')[1].split('&')[0] : youtubeVideo.url.split('/').pop()}`}
                      title="YouTube video preview"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      style={{position: 'absolute', top: 0, left: 0, width: '100%', height: '100%'}}
                    ></iframe>
                  </div>
                </div>
              )}
              <button onClick={handleYouTubeSave} className="btn-submit">
                Save YouTube Video
              </button>
              <button onClick={() => setActiveSection(null)} className="btn-submit-small" style={{marginLeft: '1rem', background: 'rgba(255, 77, 0, 0.2)', border: '2px solid var(--primary)'}}>
                ← Back to Sections
              </button>
            </div>
          </div>
        )}

        {/* Pricing Section */}
        {activeTab === 'main' && activeSection === 'pricing' && (
          <div className="admin-section">
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem'}}>
              <div style={{display: 'flex', alignItems: 'center', gap: '1rem'}}>
                <ArrowLeft 
                  size={32} 
                  style={{cursor: 'pointer', color: 'var(--primary)'}} 
                  onClick={() => setActiveSection(null)}
                />
                <h2 className="section-title-admin" style={{marginBottom: 0}}>Pricing Plans</h2>
              </div>
              <button 
                onClick={() => setShowNewPricingForm(!showNewPricingForm)} 
                className="btn-submit-small"
                style={{marginLeft: 'auto'}}
              >
                {showNewPricingForm ? 'Cancel' : '+ Add New Plan'}
              </button>
            </div>

            {showNewPricingForm && (
              <div className="admin-card" style={{marginBottom: '2rem'}}>
                <h3 className="card-title">Add New Pricing Plan</h3>
                <form onSubmit={handleAddPricing}>
                  <div className="form-group">
                    <label className="form-label">Title *</label>
                    <input
                      type="text"
                      value={newPricing.title}
                      onChange={(e) => setNewPricing({...newPricing, title: e.target.value})}
                      className="form-input"
                      placeholder="e.g., Premium Package"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Price *</label>
                    <input
                      type="text"
                      value={newPricing.price}
                      onChange={(e) => setNewPricing({...newPricing, price: e.target.value})}
                      className="form-input"
                      placeholder="e.g., $199"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Period *</label>
                    <input
                      type="text"
                      value={newPricing.period}
                      onChange={(e) => setNewPricing({...newPricing, period: e.target.value})}
                      className="form-input"
                      placeholder="e.g., per month"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Features (one per line) *</label>
                    <textarea
                      value={newPricing.features}
                      onChange={(e) => setNewPricing({...newPricing, features: e.target.value})}
                      className="form-textarea"
                      rows="6"
                      placeholder="Unlimited sessions&#10;Personalized meal plan&#10;Weekly check-ins&#10;24/7 support"
                      required
                    ></textarea>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Calendly URL</label>
                    <input
                      type="url"
                      value={newPricing.calendlyUrl || ''}
                      onChange={(e) => setNewPricing({...newPricing, calendlyUrl: e.target.value})}
                      className="form-input"
                      placeholder="https://calendly.com/bodyworksmiami/..."
                    />
                    <small style={{display: 'block', marginTop: '0.5rem', color: 'rgba(245, 245, 245, 0.6)'}}>
                      Calendly link for "Get Started" button
                    </small>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Learn More Link (Optional)</label>
                    <input
                      type="text"
                      value={newPricing.learnMoreUrl || ''}
                      onChange={(e) => setNewPricing({...newPricing, learnMoreUrl: e.target.value})}
                      className="form-input"
                      placeholder="/group or /mobile or full URL"
                    />
                    <small style={{display: 'block', marginTop: '0.5rem', color: 'rgba(245, 245, 245, 0.6)'}}>
                      Enter /group, /mobile, or a full URL. Users will be redirected when clicking "Learn More"
                    </small>
                  </div>
                  <div className="form-group">
                    <label className="form-label">
                      <input
                        type="checkbox"
                        checked={newPricing.featured}
                        onChange={(e) => setNewPricing({...newPricing, featured: e.target.checked})}
                        style={{marginRight: '0.5rem'}}
                      />
                      Featured Plan (Shows "Most Popular" badge)
                    </label>
                  </div>
                  <button type="submit" className="btn-submit">
                    Add Pricing Plan
                  </button>
                </form>
              </div>
            )}

            {pricing.length === 0 && !showNewPricingForm ? (
              <div className="admin-card">
                <p style={{marginBottom: '1rem', color: 'rgba(245, 245, 245, 0.7)'}}>
                  No pricing plans found. Click "+ Add New Plan" to create one.
                </p>
              </div>
            ) : (
              <div className="services-admin-grid">
                {pricing.map((plan) => (
                  <div key={plan.id} className="admin-card">
                    <h3 className="card-title">{plan.title}</h3>
                    <div className="form-group">
                      <label className="form-label">Title</label>
                      <input
                        type="text"
                        value={plan.title}
                        onChange={(e) => handlePricingUpdate(plan.id, 'title', e.target.value)}
                        className="form-input"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Price</label>
                      <input
                        type="text"
                        value={plan.price}
                        onChange={(e) => handlePricingUpdate(plan.id, 'price', e.target.value)}
                        className="form-input"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Period</label>
                      <input
                        type="text"
                        value={plan.period}
                        onChange={(e) => handlePricingUpdate(plan.id, 'period', e.target.value)}
                        className="form-input"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Features (one per line)</label>
                      <textarea
                        value={Array.isArray(plan.features) ? plan.features.join('\n') : plan.features}
                        onChange={(e) => handlePricingUpdate(plan.id, 'features', e.target.value)}
                        className="form-textarea"
                        rows="6"
                      ></textarea>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Calendly URL</label>
                      <input
                        type="url"
                        value={plan.calendlyUrl || ''}
                        onChange={(e) => handlePricingUpdate(plan.id, 'calendlyUrl', e.target.value)}
                        className="form-input"
                        placeholder="https://calendly.com/bodyworksmiami/..."
                      />
                      <small style={{display: 'block', marginTop: '0.5rem', color: 'rgba(245, 245, 245, 0.6)'}}>
                        Calendly link for "Get Started" button
                      </small>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Learn More Link (Optional)</label>
                      <input
                        type="text"
                        value={plan.learnMoreUrl || ''}
                        onChange={(e) => handlePricingUpdate(plan.id, 'learnMoreUrl', e.target.value)}
                        className="form-input"
                        placeholder="/group or /mobile or full URL"
                      />
                      <small style={{display: 'block', marginTop: '0.5rem', color: 'rgba(245, 245, 245, 0.6)'}}>
                        Enter /group, /mobile, or a full URL. Users will be redirected when clicking "Learn More"
                      </small>
                    </div>
                    <div className="form-group">
                      <label className="form-label">
                        <input
                          type="checkbox"
                          checked={plan.featured || false}
                          onChange={(e) => handlePricingUpdate(plan.id, 'featured', e.target.checked)}
                          style={{marginRight: '0.5rem'}}
                        />
                        Featured Plan
                      </label>
                    </div>
                    <div style={{display: 'flex', gap: '0.5rem'}}>
                      <button onClick={() => handlePricingSave(plan.id)} className="btn-submit-small" style={{flex: 1}}>
                        Save Changes
                      </button>
                      <button 
                        onClick={() => handleDeletePricing(plan.id)} 
                        className="btn-submit-small"
                        style={{
                          flex: 1,
                          background: 'linear-gradient(135deg, #ff6b6b 0%, #ff4757 100%)'
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
              ))}
            </div>
          )}
            <button onClick={() => setActiveSection(null)} className="btn-submit" style={{marginTop: '2rem'}}>
              ← Back to Sections
            </button>
          </div>
        )}

        {/* Contact Section */}
        {activeTab === 'main' && activeSection === 'contact' && (
          <div className="admin-section">
            <div style={{display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '2rem'}}>
              <ArrowLeft 
                size={32} 
                style={{cursor: 'pointer', color: 'var(--primary)'}} 
                onClick={() => setActiveSection(null)}
              />
              <h2 className="section-title-admin" style={{marginBottom: 0}}>Contact Section</h2>
            </div>
            <div className="admin-card">
              <div className="form-group">
                <label htmlFor="contact-title" className="form-label">Title</label>
                <input
                  type="text"
                  id="contact-title"
                  value={contact.title}
                  onChange={(e) => setContact({ ...contact, title: e.target.value })}
                  className="form-input"
                  placeholder="Ready to Start?"
                />
              </div>
              <div className="form-group">
                <label htmlFor="contact-subtitle" className="form-label">Subtitle</label>
                <textarea
                  id="contact-subtitle"
                  value={contact.subtitle}
                  onChange={(e) => setContact({ ...contact, subtitle: e.target.value })}
                  className="form-textarea"
                  rows="3"
                  placeholder="Enter contact subtitle..."
                ></textarea>
              </div>
              <button onClick={handleContactSave} className="btn-submit">
                Save Contact Section
              </button>
              <button onClick={() => setActiveSection(null)} className="btn-submit-small" style={{marginLeft: '1rem', background: 'rgba(255, 77, 0, 0.2)', border: '2px solid var(--primary)'}}>
                ← Back to Sections
              </button>
            </div>
          </div>
        )}

        {/* Footer Section */}
        {activeTab === 'main' && activeSection === 'footer' && (
          <div className="admin-section">
            <div style={{display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '2rem'}}>
              <ArrowLeft 
                size={32} 
                style={{cursor: 'pointer', color: 'var(--primary)'}} 
                onClick={() => setActiveSection(null)}
              />
              <h2 className="section-title-admin" style={{marginBottom: 0}}>Footer</h2>
            </div>
            <div className="admin-card">
              <div className="form-group">
                <label htmlFor="footer-copyright" className="form-label">Copyright Text</label>
                <input
                  type="text"
                  id="footer-copyright"
                  value={footer.copyright}
                  onChange={(e) => setFooter({ ...footer, copyright: e.target.value })}
                  className="form-input"
                  placeholder="© 2024 Carlo Personal Training. All rights reserved."
                />
              </div>
              <div className="form-group">
                <label htmlFor="footer-instagram" className="form-label">Instagram URL</label>
                <input
                  type="url"
                  id="footer-instagram"
                  value={footer.instagramUrl || ''}
                  onChange={(e) => setFooter({ ...footer, instagramUrl: e.target.value })}
                  className="form-input"
                  placeholder="https://www.instagram.com/body_workstraining?igsh=..."
                />
              </div>
              <div className="form-group">
                <label htmlFor="footer-tiktok" className="form-label">TikTok URL</label>
                <input
                  type="url"
                  id="footer-tiktok"
                  value={footer.tiktokUrl || ''}
                  onChange={(e) => setFooter({ ...footer, tiktokUrl: e.target.value })}
                  className="form-input"
                  placeholder="https://tiktok.com/@bodyworkstraining"
                />
              </div>
              <div className="form-group">
                <label htmlFor="footer-facebook" className="form-label">Facebook URL</label>
                <input
                  type="url"
                  id="footer-facebook"
                  value={footer.facebookUrl || ''}
                  onChange={(e) => setFooter({ ...footer, facebookUrl: e.target.value })}
                  className="form-input"
                  placeholder="https://facebook.com/bodyworkstraining"
                />
              </div>
              <small style={{display: 'block', marginTop: '0.5rem', color: 'rgba(245, 245, 245, 0.6)'}}>
                These social media links will appear above the CTA on all pages
              </small>
            </div>
            <button onClick={handleFooterSave} className="btn-submit">
              Save Footer
            </button>
            <button onClick={() => setActiveSection(null)} className="btn-submit-small" style={{marginLeft: '1rem', background: 'rgba(255, 77, 0, 0.2)', border: '2px solid var(--primary)'}}>
              ← Back to Sections
            </button>
          </div>
        )}

        {/* Services Section */}
        {activeTab === 'main' && activeSection === 'services' && (
          <div className="admin-section">
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem'}}>
              <div style={{display: 'flex', alignItems: 'center', gap: '1rem'}}>
                <ArrowLeft 
                  size={32} 
                  style={{cursor: 'pointer', color: 'var(--primary)'}} 
                  onClick={() => setActiveSection(null)}
                />
                <h2 className="section-title-admin" style={{marginBottom: 0}}>Services</h2>
              </div>
              <button 
                onClick={() => setShowNewServiceForm(!showNewServiceForm)} 
                className="btn-submit-small"
                style={{marginLeft: 'auto'}}
              >
                {showNewServiceForm ? 'Cancel' : '+ Add New Service'}
              </button>
            </div>

            {showNewServiceForm && (
              <div className="admin-card" style={{marginBottom: '2rem'}}>
                <h3 className="card-title">Add New Service</h3>
                <form onSubmit={handleAddService}>
                  <div className="form-group">
                    <label className="form-label">Title *</label>
                    <input
                      type="text"
                      value={newService.title}
                      onChange={(e) => setNewService({...newService, title: e.target.value})}
                      className="form-input"
                      required
                      placeholder="e.g., Weight Loss"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Description *</label>
                    <textarea
                      value={newService.description}
                      onChange={(e) => setNewService({...newService, description: e.target.value})}
                      className="form-textarea"
                      rows="4"
                      required
                      placeholder="Describe this service..."
                    ></textarea>
                  </div>
                  <button type="submit" className="btn-submit">Add Service</button>
                </form>
              </div>
            )}

            {services.length === 0 && !showNewServiceForm ? (
              <div className="admin-card">
                <p style={{marginBottom: '1rem', color: 'rgba(245, 245, 245, 0.7)'}}>
                  No services found. Click "Add New Service" to create your first service.
                </p>
              </div>
            ) : (
              <div className="services-admin-grid">
                {services.map((service, index) => (
                  <div key={service.id} className="admin-card">
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem'}}>
                      <h3 className="card-title" style={{marginBottom: 0}}>Service {index + 1}</h3>
                      <button 
                        onClick={() => handleDeleteService(service.id)}
                        className="btn-delete"
                        style={{padding: '0.5rem 1rem', fontSize: '0.85rem'}}
                      >
                        Delete
                      </button>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Title</label>
                      <input
                        type="text"
                        value={service.title}
                        onChange={(e) => handleServiceUpdate(service.id, 'title', e.target.value)}
                        className="form-input"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Description</label>
                      <textarea
                        value={service.description}
                        onChange={(e) => handleServiceUpdate(service.id, 'description', e.target.value)}
                        className="form-textarea"
                        rows="4"
                      ></textarea>
                    </div>
                    <button onClick={() => handleServiceSave(service.id)} className="btn-submit-small">
                      Save Changes
                    </button>
                  </div>
              ))}
            </div>
          )}
            <button onClick={() => setActiveSection(null)} className="btn-submit" style={{marginTop: '2rem'}}>
              ← Back to Sections
            </button>
          </div>
        )}

        {/* Trainers Section */}
        {activeTab === 'main' && activeSection === 'trainers' && (
          <div className="admin-section">
            <div style={{display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '2rem'}}>
              <ArrowLeft 
                size={32} 
                style={{cursor: 'pointer', color: 'var(--primary)'}} 
                onClick={() => setActiveSection(null)}
              />
              <h2 className="section-title-admin" style={{marginBottom: 0}}>
                {editingTrainerId ? 'Edit Trainer' : 'Add New Trainer'}
              </h2>
            </div>
            <div className="admin-card">
              <form onSubmit={handleTrainerSubmit} className="trainer-form">
                <div className="form-group">
                  <label htmlFor="name" className="form-label">Name *</label>
                  <input
                    type="text"
                    id="name"
                    value={trainerForm.name}
                    onChange={(e) => setTrainerForm({ ...trainerForm, name: e.target.value })}
                    className="form-input"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="specialty" className="form-label">Specialty *</label>
                  <input
                    type="text"
                    id="specialty"
                    value={trainerForm.specialty}
                    onChange={(e) => setTrainerForm({ ...trainerForm, specialty: e.target.value })}
                    className="form-input"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="bio" className="form-label">Bio</label>
                  <textarea
                    id="bio"
                    value={trainerForm.bio}
                    onChange={(e) => setTrainerForm({ ...trainerForm, bio: e.target.value })}
                    className="form-textarea"
                    rows="4"
                  ></textarea>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="experience" className="form-label">Years of Experience</label>
                    <input
                      type="number"
                      id="experience"
                      value={trainerForm.experience}
                      onChange={(e) => setTrainerForm({ ...trainerForm, experience: e.target.value })}
                      className="form-input"
                    />
                  </div>
                <div className="form-group">
                  <label className="form-label">Trainer Photo</label>
                  <ImageUpload
                    currentImageUrl={trainerForm.imageUrl}
                    onUploadComplete={(url) => setTrainerForm({ ...trainerForm, imageUrl: url })}
                    folder="trainers"
                  />
                </div>
                </div>
                <div className="form-group">
                  <label htmlFor="certifications" className="form-label">Certifications</label>
                  <input
                    type="text"
                    id="certifications"
                    value={trainerForm.certifications}
                    onChange={(e) => setTrainerForm({ ...trainerForm, certifications: e.target.value })}
                    className="form-input"
                    placeholder="Separate with commas"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="calendlyUrl" className="form-label">Calendly Booking URL</label>
                  <input
                    type="url"
                    id="calendlyUrl"
                    value={trainerForm.calendlyUrl}
                    onChange={(e) => setTrainerForm({ ...trainerForm, calendlyUrl: e.target.value })}
                    className="form-input"
                    placeholder="https://calendly.com/your-username"
                  />
                  <small style={{display: 'block', marginTop: '0.5rem', color: 'rgba(245, 245, 245, 0.6)'}}>
                    Add your Calendly scheduling link (e.g., https://calendly.com/your-username)
                  </small>
                </div>
                <div className="form-actions">
                  <button type="submit" className="btn-submit">
                    {editingTrainerId ? 'Update Trainer' : 'Add Trainer'}
                  </button>
                  {editingTrainerId && (
                    <button
                      type="button"
                      onClick={() => {
                        setEditingTrainerId(null);
                        setTrainerForm({ name: '', specialty: '', bio: '', experience: '', certifications: '', imageUrl: '' });
                      }}
                      className="btn-cancel"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </form>
            </div>

            <h2 className="section-title-admin" style={{marginTop: '3rem'}}>Current Trainers</h2>
            <div className="trainers-admin-list">
              {trainers.map((trainer) => (
                <div key={trainer.id} className="trainer-admin-item">
                  <div className="trainer-admin-info">
                    <h3>{trainer.name}</h3>
                    <p>{trainer.specialty}</p>
                  </div>
                  <div className="trainer-admin-actions">
                    <button onClick={() => handleEditTrainer(trainer)} className="btn-edit">
                      Edit
                    </button>
                    <button onClick={() => handleDeleteTrainer(trainer.id)} className="btn-delete">
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <button onClick={() => setActiveSection(null)} className="btn-submit" style={{marginTop: '2rem'}}>
              ← Back to Sections
            </button>
          </div>
        )}

        {/* Reviews Section */}
        {activeTab === 'main' && activeSection === 'reviews' && (
          <div className="admin-section">
            <div style={{display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '2rem'}}>
              <ArrowLeft 
                size={32} 
                style={{cursor: 'pointer', color: 'var(--primary)'}} 
                onClick={() => setActiveSection(null)}
              />
              <h2 className="section-title-admin" style={{marginBottom: 0}}>Client Reviews</h2>
            </div>
            <button onClick={handleAddReview} className="add-button" style={{marginBottom: '2rem'}}>
              + Add New Review
            </button>
            
            {reviews.map((review, index) => (
              <div key={review.id} className="admin-card" style={{marginBottom: '2rem'}}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem'}}>
                  <h3 style={{margin: 0}}>Review #{index + 1}</h3>
                  <button
                    onClick={() => handleDeleteReview(review.id)}
                    className="delete-button"
                  >
                    Delete
                  </button>
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <input
                      type="checkbox"
                      checked={review.active !== false}
                      onChange={(e) => handleReviewUpdate(review.id, 'active', e.target.checked)}
                      style={{marginRight: '0.5rem'}}
                    />
                    Display on Website
                  </label>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Client Name *</label>
                    <input
                      type="text"
                      value={review.name || ''}
                      onChange={(e) => handleReviewUpdate(review.id, 'name', e.target.value)}
                      className="form-input"
                      placeholder="John Doe"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label">Rating (1-5) *</label>
                    <input
                      type="number"
                      min="1"
                      max="5"
                      value={review.rating || 5}
                      onChange={(e) => handleReviewUpdate(review.id, 'rating', e.target.value)}
                      className="form-input"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Display Order</label>
                    <input
                      type="number"
                      value={review.order || 0}
                      onChange={(e) => handleReviewUpdate(review.id, 'order', parseInt(e.target.value))}
                      className="form-input"
                      placeholder="0"
                    />
                    <small style={{display: 'block', marginTop: '0.5rem', color: 'rgba(245, 245, 245, 0.6)'}}>
                      Higher numbers appear first
                    </small>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Review Text *</label>
                  <textarea
                    value={review.text || ''}
                    onChange={(e) => handleReviewUpdate(review.id, 'text', e.target.value)}
                    className="form-textarea"
                    rows="4"
                    placeholder="Write the client's review here..."
                  ></textarea>
                </div>

                <div className="form-group">
                  <label className="form-label">Achievement Badge (Optional)</label>
                  <input
                    type="text"
                    value={review.achievement || ''}
                    onChange={(e) => handleReviewUpdate(review.id, 'achievement', e.target.value)}
                    className="form-input"
                    placeholder="e.g., Lost 30 lbs, Gained 15 lbs muscle"
                  />
                  <small style={{display: 'block', marginTop: '0.5rem', color: 'rgba(245, 245, 245, 0.6)'}}>
                    A short achievement to highlight
                  </small>
                </div>

                <div className="form-group">
                  <label className="form-label">Client Photo (Optional)</label>
                  <ImageUpload
                    currentImageUrl={review.imageUrl}
                    onUploadComplete={(url) => handleReviewUpdate(review.id, 'imageUrl', url)}
                    folder="reviews"
                  />
                  <small style={{display: 'block', marginTop: '0.5rem', color: 'rgba(245, 245, 245, 0.6)'}}>
                    If no photo, initials will be displayed
                  </small>
                </div>

                <button
                  onClick={() => handleReviewSave(review.id)}
                  className="save-button"
                >
                  Save Review
                </button>
              </div>
            ))}

            {reviews.length === 0 && (
              <p style={{textAlign: 'center', color: 'rgba(245, 245, 245, 0.6)', padding: '3rem 0'}}>
                No reviews yet. Click "Add New Review" to create one.
              </p>
            )}
          </div>
        )}

        {/* Mobile Training Section */}
        {activeTab === 'main' && activeSection === 'mobile' && (
          <div className="admin-section">
            <div style={{display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '2rem'}}>
              <ArrowLeft 
                size={32} 
                style={{cursor: 'pointer', color: 'var(--primary)'}} 
                onClick={() => setActiveSection(null)}
              />
              <h2 className="section-title-admin" style={{marginBottom: 0}}>Anywhere, Anytime Section</h2>
            </div>
            <div className="admin-card">
              <div className="form-group">
                <label className="form-label">Title</label>
                <input
                  type="text"
                  value={mobileTraining.title || ''}
                  onChange={(e) => setMobileTraining({...mobileTraining, title: e.target.value})}
                  className="form-input"
                  placeholder="e.g., Anywhere, Anytime"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Subtitle</label>
                <input
                  type="text"
                  value={mobileTraining.subtitle || ''}
                  onChange={(e) => setMobileTraining({...mobileTraining, subtitle: e.target.value})}
                  className="form-input"
                  placeholder="e.g., We Come To You"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea
                  value={mobileTraining.description || ''}
                  onChange={(e) => setMobileTraining({...mobileTraining, description: e.target.value})}
                  className="form-textarea"
                  rows="5"
                  placeholder="Describe your mobile training service..."
                ></textarea>
              </div>
              <div className="form-group">
                <label className="form-label">Features (one per line)</label>
                <textarea
                  value={Array.isArray(mobileTraining.features) ? mobileTraining.features.join('\n') : mobileTraining.features || ''}
                  onChange={(e) => setMobileTraining({...mobileTraining, features: e.target.value})}
                  className="form-textarea"
                  rows="6"
                  placeholder="Train in the comfort of your home&#10;Flexible scheduling around your life&#10;No gym membership required"
                ></textarea>
              </div>
              <div className="form-group">
                <label className="form-label">Section Image</label>
                <ImageUpload
                  currentImageUrl={mobileTraining.imageUrl || ''}
                  onUploadComplete={(url) => setMobileTraining({...mobileTraining, imageUrl: url})}
                  folder="mobile-training"
                />
              </div>
              <button onClick={handleMobileTrainingSave} className="btn-submit-small">
                Save Mobile Training Section
              </button>
            </div>
          </div>
        )}

        {/* Locations Section */}
        {activeTab === 'main' && activeSection === 'locations' && (
          <div className="admin-section">
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem'}}>
              <div style={{display: 'flex', alignItems: 'center', gap: '1rem'}}>
                <ArrowLeft 
                  size={32} 
                  style={{cursor: 'pointer', color: 'var(--primary)'}} 
                  onClick={() => setActiveSection(null)}
                />
                <h2 className="section-title-admin" style={{marginBottom: 0}}>Training Locations</h2>
              </div>
              <button 
                onClick={() => setShowNewLocationForm(!showNewLocationForm)} 
                className="btn-submit-small"
                style={{marginLeft: 'auto'}}
              >
                {showNewLocationForm ? 'Cancel' : '+ Add New Location'}
              </button>
            </div>

            {showNewLocationForm && (
              <div className="admin-card" style={{marginBottom: '2rem'}}>
                <h3 className="card-title">Add New Location</h3>
                <form onSubmit={handleAddLocation}>
                  <div className="form-group">
                    <label className="form-label">Location Name *</label>
                    <input
                      type="text"
                      value={newLocation.name}
                      onChange={(e) => setNewLocation({...newLocation, name: e.target.value})}
                      className="form-input"
                      placeholder="e.g., Lifetime Fitness Pinecrest"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Address *</label>
                    <input
                      type="text"
                      value={newLocation.address}
                      onChange={(e) => setNewLocation({...newLocation, address: e.target.value})}
                      className="form-input"
                      placeholder="e.g., 12451 N Kendall Dr, Miami, FL 33186"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Description *</label>
                    <textarea
                      value={newLocation.description}
                      onChange={(e) => setNewLocation({...newLocation, description: e.target.value})}
                      className="form-textarea"
                      rows="3"
                      placeholder="Brief description of the location"
                      required
                    ></textarea>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Hours *</label>
                    <input
                      type="text"
                      value={newLocation.hours}
                      onChange={(e) => setNewLocation({...newLocation, hours: e.target.value})}
                      className="form-input"
                      placeholder="e.g., Mon-Fri: 6am-8pm"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Features (one per line) *</label>
                    <textarea
                      value={newLocation.features}
                      onChange={(e) => setNewLocation({...newLocation, features: e.target.value})}
                      className="form-textarea"
                      rows="4"
                      placeholder="Full Equipment&#10;Group Classes&#10;Private Training Areas"
                      required
                    ></textarea>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Google Maps Link</label>
                    <input
                      type="url"
                      value={newLocation.mapLink}
                      onChange={(e) => setNewLocation({...newLocation, mapLink: e.target.value})}
                      className="form-input"
                      placeholder="https://maps.google.com/?q=YourLocation"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">
                      <input
                        type="checkbox"
                        checked={newLocation.featured}
                        onChange={(e) => setNewLocation({...newLocation, featured: e.target.checked})}
                        style={{marginRight: '0.5rem'}}
                      />
                      Featured Location (Shows "Most Popular" badge)
                    </label>
                  </div>
                  <button type="submit" className="btn-submit">
                    Add Location
                  </button>
                </form>
              </div>
            )}

            {locations.length === 0 && !showNewLocationForm ? (
              <div className="admin-card">
                <p style={{marginBottom: '1rem', color: 'rgba(245, 245, 245, 0.7)'}}>
                  No locations found. Click "+ Add New Location" to create one.
                </p>
              </div>
            ) : (
              <div className="services-admin-grid">
                {locations.map((location) => (
                  <div key={location.id} className="admin-card">
                    <h3 className="card-title">{location.name}</h3>
                    <div className="form-group">
                      <label className="form-label">Location Name</label>
                      <input
                        type="text"
                        value={location.name}
                        onChange={(e) => handleLocationUpdate(location.id, 'name', e.target.value)}
                        className="form-input"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Address</label>
                      <input
                        type="text"
                        value={location.address}
                        onChange={(e) => handleLocationUpdate(location.id, 'address', e.target.value)}
                        className="form-input"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Description</label>
                      <textarea
                        value={location.description}
                        onChange={(e) => handleLocationUpdate(location.id, 'description', e.target.value)}
                        className="form-textarea"
                        rows="3"
                      ></textarea>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Hours</label>
                      <input
                        type="text"
                        value={location.hours}
                        onChange={(e) => handleLocationUpdate(location.id, 'hours', e.target.value)}
                        className="form-input"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Features (one per line)</label>
                      <textarea
                        value={Array.isArray(location.features) ? location.features.join('\n') : location.features}
                        onChange={(e) => handleLocationUpdate(location.id, 'features', e.target.value)}
                        className="form-textarea"
                        rows="4"
                      ></textarea>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Google Maps Link</label>
                      <input
                        type="url"
                        value={location.mapLink}
                        onChange={(e) => handleLocationUpdate(location.id, 'mapLink', e.target.value)}
                        className="form-input"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">
                        <input
                          type="checkbox"
                          checked={location.featured || false}
                          onChange={(e) => handleLocationUpdate(location.id, 'featured', e.target.checked)}
                          style={{marginRight: '0.5rem'}}
                        />
                        Featured Location
                      </label>
                    </div>
                    <div style={{display: 'flex', gap: '0.5rem'}}>
                      <button onClick={() => handleLocationSave(location.id)} className="btn-submit-small" style={{flex: 1}}>
                        Save Changes
                      </button>
                      <button 
                        onClick={() => handleDeleteLocation(location.id)} 
                        className="btn-submit-small"
                        style={{
                          flex: 1,
                          background: 'linear-gradient(135deg, #ff6b6b 0%, #ff4757 100%)'
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="admin-card" style={{marginTop: '3rem'}}>
              <h3 className="card-title">Service Areas Section</h3>
              <div className="form-group">
                <label className="form-label">Section Title</label>
                <input
                  type="text"
                  value={serviceAreas?.title || ''}
                  onChange={(e) => setServiceAreas({...serviceAreas, title: e.target.value})}
                  className="form-input"
                  placeholder="e.g., Service Areas"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Description (use &lt;strong&gt; for bold)</label>
                <textarea
                  value={serviceAreas?.description || ''}
                  onChange={(e) => setServiceAreas({...serviceAreas, description: e.target.value})}
                  className="form-textarea"
                  rows="4"
                  placeholder="Proudly serving <strong>Pinecrest</strong>, <strong>Kendall</strong>..."
                ></textarea>
              </div>
              <div className="form-group">
                <label className="form-label">Online Training Notice</label>
                <input
                  type="text"
                  value={serviceAreas?.onlineNotice || ''}
                  onChange={(e) => setServiceAreas({...serviceAreas, onlineNotice: e.target.value})}
                  className="form-input"
                  placeholder="e.g., Online training available nationwide"
                />
              </div>
              <button onClick={handleServiceAreasSave} className="btn-submit-small">
                Save Service Areas
              </button>
            </div>
            <button onClick={() => setActiveSection(null)} className="btn-submit" style={{marginTop: '2rem'}}>
              ← Back to Sections
            </button>
          </div>
        )}

        {/* Group Page - Section Grid */}
        {activeTab === 'group-page' && !activeGroupSection && (
          <div className="admin-section">
            <h2 className="section-title-admin">Group Page Sections</h2>
            <div className="main-sections-grid">
              <div className="section-card" onClick={() => setActiveGroupSection('hero')}>
                <h3>Hero Section</h3>
                <p>Page header and CTA</p>
              </div>
              <div className="section-card" onClick={() => setActiveGroupSection('video')}>
                <h3>YouTube Video</h3>
                <p>Featured video at top</p>
              </div>
              <div className="section-card" onClick={() => setActiveGroupSection('whofor')}>
                <h3>Who It's For</h3>
                <p>Target audience section</p>
              </div>
              <div className="section-card" onClick={() => setActiveGroupSection('schedule')}>
                <h3>Schedule & Location</h3>
                <p>Training times and address</p>
              </div>
              <div className="section-card" onClick={() => setActiveGroupSection('packages')}>
                <h3>Training Packages</h3>
                <p>Manage package offerings</p>
              </div>
              <div className="section-card" onClick={() => setActiveGroupSection('gallery')}>
                <h3>Gallery</h3>
                <p>Photo gallery for page</p>
              </div>
              <div className="section-card" onClick={() => setActiveGroupSection('cta')}>
                <h3>Final CTA</h3>
                <p>Bottom call-to-action</p>
              </div>
            </div>
          </div>
        )}

        {/* Group Page - Hero Section */}
        {activeTab === 'group-page' && activeGroupSection === 'hero' && (
          <div className="admin-section">
            <div style={{display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '2rem'}}>
              <ArrowLeft 
                size={32} 
                style={{cursor: 'pointer', color: 'var(--primary)'}} 
                onClick={() => setActiveGroupSection(null)}
              />
              <h2 className="section-title-admin" style={{marginBottom: 0}}>Group Page - Hero Section</h2>
            </div>
            
            <div className="admin-card" style={{marginBottom: '2rem'}}>
              <h3 style={{marginBottom: '1.5rem'}}>Hero Section</h3>
              <div className="form-group">
                <label className="form-label">Page Title</label>
                <input
                  type="text"
                  value={groupHero.title || ''}
                  onChange={(e) => setGroupHero({...groupHero, title: e.target.value})}
                  className="form-input"
                  placeholder="GROUP TRAINING"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Subtitle</label>
                <input
                  type="text"
                  value={groupHero.subtitle || ''}
                  onChange={(e) => setGroupHero({...groupHero, subtitle: e.target.value})}
                  className="form-input"
                  placeholder="Build Community While Building Strength"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Button Text</label>
                <input
                  type="text"
                  value={groupHero.buttonText || ''}
                  onChange={(e) => setGroupHero({...groupHero, buttonText: e.target.value})}
                  className="form-input"
                  placeholder="View Packages"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Hero background video (MP4)</label>
                <p style={{ marginBottom: '0.75rem', fontSize: '0.85rem', color: 'rgba(245,245,245,0.65)' }}>
                  Upload an MP4 or paste a URL. Leave empty for a solid black hero.
                </p>
                <VideoUpload
                  folder="group-hero-background"
                  currentVideoUrl={groupHero.backgroundVideoUrl || ''}
                  onUploadComplete={(url) => setGroupHero({ ...groupHero, backgroundVideoUrl: url })}
                />
                <label className="form-label" style={{ marginTop: '1rem' }}>Video URL (optional)</label>
                <input
                  type="url"
                  value={groupHero.backgroundVideoUrl || ''}
                  onChange={(e) => setGroupHero({ ...groupHero, backgroundVideoUrl: e.target.value })}
                  className="form-input"
                  placeholder="https://…"
                />
              </div>
              <button onClick={async () => {
                try {
                  await setDoc(doc(db, 'settings', 'groupHero'), {
                    ...groupHero,
                    backgroundVideoUrl: sanitizeURL(groupHero.backgroundVideoUrl || ''),
                    updatedAt: serverTimestamp()
                  });
                  alert('Group hero updated!');
                } catch (error) {
                  alert(error.message);
                }
              }} className="save-button">Save Hero</button>
              <button onClick={() => setActiveGroupSection(null)} className="btn-submit-small" style={{marginLeft: '1rem', background: 'rgba(255, 77, 0, 0.2)', border: '2px solid var(--primary)'}}>
                ← Back to Sections
              </button>
            </div>
          </div>
        )}

        {/* Group Page - Video Section */}
        {activeTab === 'group-page' && activeGroupSection === 'video' && (
          <div className="admin-section">
            <div style={{display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '2rem'}}>
              <ArrowLeft 
                size={32} 
                style={{cursor: 'pointer', color: 'var(--primary)'}} 
                onClick={() => setActiveGroupSection(null)}
              />
              <h2 className="section-title-admin" style={{marginBottom: 0}}>Group Page - YouTube Video</h2>
            </div>
            <div className="admin-card" style={{marginBottom: '2rem'}}>
              <div className="form-group">
                <label className="form-label">YouTube URL</label>
                <input
                  type="url"
                  value={groupVideo.url || ''}
                  onChange={(e) => setGroupVideo({...groupVideo, url: e.target.value})}
                  className="form-input"
                  placeholder="https://www.youtube.com/watch?v=..."
                />
                <small style={{display: 'block', marginTop: '0.5rem', color: 'rgba(245, 245, 245, 0.6)'}}>
                  Paste full YouTube URL
                </small>
              </div>
              <div className="form-group">
                <label className="form-label">Video Title (Optional)</label>
                <input
                  type="text"
                  value={groupVideo.title || ''}
                  onChange={(e) => setGroupVideo({...groupVideo, title: e.target.value})}
                  className="form-input"
                  placeholder="See Our Group Sessions in Action"
                />
              </div>
              <button onClick={async () => {
                try {
                  await setDoc(doc(db, 'settings', 'groupVideo'), {...groupVideo, updatedAt: serverTimestamp()});
                  alert('Video updated!');
                } catch (error) {
                  alert(error.message);
                }
              }} className="save-button">Save Video</button>
              <button onClick={() => setActiveGroupSection(null)} className="btn-submit-small" style={{marginLeft: '1rem', background: 'rgba(255, 77, 0, 0.2)', border: '2px solid var(--primary)'}}>
                ← Back to Sections
              </button>
            </div>
          </div>
        )}

        {/* Group Page - Who For Section */}
        {activeTab === 'group-page' && activeGroupSection === 'whofor' && (
          <div className="admin-section">
            <div style={{display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '2rem'}}>
              <ArrowLeft 
                size={32} 
                style={{cursor: 'pointer', color: 'var(--primary)'}} 
                onClick={() => setActiveGroupSection(null)}
              />
              <h2 className="section-title-admin" style={{marginBottom: 0}}>Group Page - Who It's For</h2>
            </div>
            <div className="admin-card" style={{marginBottom: '2rem'}}>
              <div className="form-group">
                <label className="form-label">Title</label>
                <input
                  type="text"
                  value={groupWhoFor.title || ''}
                  onChange={(e) => setGroupWhoFor({...groupWhoFor, title: e.target.value})}
                  className="form-input"
                  placeholder="Who Is This For?"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Content</label>
                <textarea
                  value={groupWhoFor.content || ''}
                  onChange={(e) => setGroupWhoFor({...groupWhoFor, content: e.target.value})}
                  className="form-textarea"
                  rows="6"
                  placeholder="Describe who group training is perfect for..."
                ></textarea>
              </div>
              <div className="form-group">
                <label className="form-label">Features/Bullet Points (one per line)</label>
                <textarea
                  value={Array.isArray(groupWhoFor.features) ? groupWhoFor.features.join('\n') : (groupWhoFor.features || '')}
                  onChange={(e) => setGroupWhoFor({...groupWhoFor, features: e.target.value})}
                  className="form-textarea"
                  rows="5"
                  placeholder="Beginners welcome&#10;Small group setting&#10;Flexible scheduling&#10;All fitness levels"
                ></textarea>
                <small style={{display: 'block', marginTop: '0.5rem', color: 'rgba(245, 245, 245, 0.6)'}}>
                  Enter one feature per line. Press Enter to create a new line.
                </small>
              </div>
              <div className="form-group">
                <label className="form-label">Image</label>
                <ImageUpload
                  currentImageUrl={groupWhoFor.imageUrl}
                  onUploadComplete={(url) => setGroupWhoFor({...groupWhoFor, imageUrl: url})}
                  folder="group-page"
                />
              </div>
              <button onClick={async () => {
                try {
                  const featuresToSave = typeof groupWhoFor.features === 'string' 
                    ? groupWhoFor.features.split('\n').filter(f => f.trim())
                    : groupWhoFor.features;
                  await setDoc(doc(db, 'settings', 'groupWhoFor'), {
                    ...groupWhoFor, 
                    features: featuresToSave,
                    updatedAt: serverTimestamp()
                  });
                  alert('Updated!');
                } catch (error) {
                  alert(error.message);
                }
              }} className="save-button">Save Section</button>
              <button onClick={() => setActiveGroupSection(null)} className="btn-submit-small" style={{marginLeft: '1rem', background: 'rgba(255, 77, 0, 0.2)', border: '2px solid var(--primary)'}}>
                ← Back to Sections
              </button>
            </div>
          </div>
        )}

        {/* Group Page - Schedule Section */}
        {activeTab === 'group-page' && activeGroupSection === 'schedule' && (
          <div className="admin-section">
            <div style={{display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '2rem'}}>
              <ArrowLeft 
                size={32} 
                style={{cursor: 'pointer', color: 'var(--primary)'}} 
                onClick={() => setActiveGroupSection(null)}
              />
              <h2 className="section-title-admin" style={{marginBottom: 0}}>Group Page - Schedule & Location</h2>
            </div>
            <div className="admin-card" style={{marginBottom: '2rem'}}>
              <h3 style={{marginBottom: '1.5rem'}}>Location Details</h3>
              <div className="form-group">
                <label className="form-label">Address</label>
                <input
                  type="text"
                  value={groupSchedule.address || ''}
                  onChange={(e) => setGroupSchedule({...groupSchedule, address: e.target.value})}
                  className="form-input"
                  placeholder="123 Main St, Miami, FL 33101"
                />
                <small style={{display: 'block', marginTop: '0.5rem', color: 'rgba(245, 245, 245, 0.6)'}}>
                  Full address where group classes are held
                </small>
              </div>
              <div className="form-group">
                <label className="form-label">Time Description</label>
                <textarea
                  value={groupSchedule.timeDescription || ''}
                  onChange={(e) => setGroupSchedule({...groupSchedule, timeDescription: e.target.value})}
                  className="form-textarea"
                  rows="4"
                  placeholder="Monday & Wednesday: 6:00 PM - 7:00 PM&#10;Saturday: 9:00 AM - 10:00 AM"
                />
                <small style={{display: 'block', marginTop: '0.5rem', color: 'rgba(245, 245, 245, 0.6)'}}>
                  Describe available class times. Use line breaks for multiple time slots.
                </small>
              </div>
              <button onClick={async () => {
                try {
                  await setDoc(doc(db, 'settings', 'groupSchedule'), {...groupSchedule, updatedAt: serverTimestamp()});
                  alert('Schedule updated!');
                } catch (error) {
                  alert(error.message);
                }
              }} className="save-button">Save Schedule</button>
              <button onClick={() => setActiveGroupSection(null)} className="btn-submit-small" style={{marginLeft: '1rem', background: 'rgba(255, 77, 0, 0.2)', border: '2px solid var(--primary)'}}>
                ← Back to Sections
              </button>
            </div>
          </div>
        )}

        {/* Group Page - Packages Section */}
        {activeTab === 'group-page' && activeGroupSection === 'packages' && (
          <div className="admin-section">
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem'}}>
              <div style={{display: 'flex', alignItems: 'center', gap: '1rem'}}>
                <ArrowLeft 
                  size={32} 
                  style={{cursor: 'pointer', color: 'var(--primary)'}} 
                  onClick={() => setActiveGroupSection(null)}
                />
                <h2 className="section-title-admin" style={{marginBottom: 0}}>Group Page - Training Packages</h2>
              </div>
              <button 
                onClick={() => setShowNewGroupPackageForm(!showNewGroupPackageForm)} 
                className="btn-submit-small"
                style={{marginLeft: 'auto'}}
              >
                {showNewGroupPackageForm ? 'Cancel' : '+ Add New Package'}
              </button>
            </div>

            {showNewGroupPackageForm && (
              <div className="admin-card" style={{marginBottom: '2rem'}}>
                <h3 className="card-title">Add New Package</h3>
                <form onSubmit={handleAddGroupPackage}>
                  <div className="form-group">
                    <label className="form-label">Title *</label>
                    <input
                      type="text"
                      value={newGroupPackage.title}
                      onChange={(e) => setNewGroupPackage({...newGroupPackage, title: e.target.value})}
                      className="form-input"
                      placeholder="e.g., Monthly Group Classes"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Price</label>
                    <input
                      type="text"
                      value={newGroupPackage.price}
                      onChange={(e) => setNewGroupPackage({...newGroupPackage, price: e.target.value})}
                      className="form-input"
                      placeholder="e.g., $120/month"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Description</label>
                    <textarea
                      value={newGroupPackage.description}
                      onChange={(e) => setNewGroupPackage({...newGroupPackage, description: e.target.value})}
                      className="form-textarea"
                      rows="4"
                      placeholder="Package description..."
                    ></textarea>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Features (one per line)</label>
                    <textarea
                      value={newGroupPackage.features}
                      onChange={(e) => setNewGroupPackage({...newGroupPackage, features: e.target.value})}
                      className="form-textarea"
                      rows="5"
                      placeholder="Unlimited classes&#10;Nutrition guidance&#10;Progress tracking"
                    ></textarea>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Calendly URL</label>
                    <input
                      type="url"
                      value={newGroupPackage.calendlyUrl}
                      onChange={(e) => setNewGroupPackage({...newGroupPackage, calendlyUrl: e.target.value})}
                      className="form-input"
                      placeholder="https://calendly.com/..."
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Display Order</label>
                    <input
                      type="number"
                      value={newGroupPackage.order}
                      onChange={(e) => setNewGroupPackage({...newGroupPackage, order: parseInt(e.target.value) || 0})}
                      className="form-input"
                      placeholder="0"
                    />
                  </div>
                  <button type="submit" className="btn-submit">Add Package</button>
                </form>
              </div>
            )}

            {groupPackages.length > 0 ? (
              <div className="services-admin-grid">
                {groupPackages.map((pkg) => (
                  <div key={pkg.id} className="admin-card">
                    <h3 className="card-title">{pkg.title}</h3>
                    <div className="form-group">
                      <label className="form-label">Title</label>
                      <input
                        type="text"
                        value={pkg.title || ''}
                        onChange={(e) => handleGroupPackageUpdate(pkg.id, 'title', e.target.value)}
                        className="form-input"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Price</label>
                      <input
                        type="text"
                        value={pkg.price || ''}
                        onChange={(e) => handleGroupPackageUpdate(pkg.id, 'price', e.target.value)}
                        className="form-input"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Description</label>
                      <textarea
                        value={pkg.description || ''}
                        onChange={(e) => handleGroupPackageUpdate(pkg.id, 'description', e.target.value)}
                        className="form-textarea"
                        rows="4"
                      ></textarea>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Features (one per line)</label>
                      <textarea
                        value={Array.isArray(pkg.features) ? pkg.features.join('\n') : (pkg.features || '')}
                        onChange={(e) => handleGroupPackageUpdate(pkg.id, 'features', e.target.value)}
                        className="form-textarea"
                        rows="5"
                      ></textarea>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Calendly URL</label>
                      <input
                        type="url"
                        value={pkg.calendlyUrl || ''}
                        onChange={(e) => handleGroupPackageUpdate(pkg.id, 'calendlyUrl', e.target.value)}
                        className="form-input"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Display Order</label>
                      <input
                        type="number"
                        value={pkg.order || 0}
                        onChange={(e) => handleGroupPackageUpdate(pkg.id, 'order', parseInt(e.target.value) || 0)}
                        className="form-input"
                      />
                    </div>
                    <div style={{display: 'flex', gap: '1rem'}}>
                      <button onClick={() => handleGroupPackageSave(pkg.id)} className="btn-submit-small" style={{flex: 1}}>
                        Save Changes
                      </button>
                      <button 
                        onClick={() => handleDeleteGroupPackage(pkg.id)} 
                        className="btn-submit-small"
                        style={{
                          flex: 1,
                          background: 'linear-gradient(135deg, #ff6b6b 0%, #ff4757 100%)'
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="admin-card">
                <p style={{color: 'rgba(245, 245, 245, 0.7)'}}>
                  No packages found. Click "+ Add New Package" to create your first package.
                </p>
              </div>
            )}
            <button onClick={() => setActiveGroupSection(null)} className="btn-submit" style={{marginTop: '2rem'}}>
              ← Back to Sections
            </button>
          </div>
        )}

        {/* Group Page - CTA Section */}
        {activeTab === 'group-page' && activeGroupSection === 'cta' && (
          <div className="admin-section">
            <div style={{display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '2rem'}}>
              <ArrowLeft 
                size={32} 
                style={{cursor: 'pointer', color: 'var(--primary)'}} 
                onClick={() => setActiveGroupSection(null)}
              />
              <h2 className="section-title-admin" style={{marginBottom: 0}}>Group Page - Final CTA</h2>
            </div>
            <div className="admin-card" style={{marginBottom: '2rem'}}>
              <h3 style={{marginBottom: '1.5rem'}}>Call to Action</h3>
              <div className="form-group">
                <label className="form-label">Title</label>
                <input
                  type="text"
                  value={groupFinalCTA.title || ''}
                  onChange={(e) => setGroupFinalCTA({...groupFinalCTA, title: e.target.value})}
                  className="form-input"
                  placeholder="Ready to Join?"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Subtitle</label>
                <input
                  type="text"
                  value={groupFinalCTA.subtitle || ''}
                  onChange={(e) => setGroupFinalCTA({...groupFinalCTA, subtitle: e.target.value})}
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Calendly URL</label>
                <input
                  type="url"
                  value={groupFinalCTA.calendlyUrl || ''}
                  onChange={(e) => setGroupFinalCTA({...groupFinalCTA, calendlyUrl: e.target.value})}
                  className="form-input"
                  placeholder="https://calendly.com/bodyworksmiami/group-class"
                />
              </div>
              <button onClick={async () => {
                try {
                  await setDoc(doc(db, 'settings', 'groupFinalCTA'), {...groupFinalCTA, updatedAt: serverTimestamp()});
                  alert('Updated!');
                } catch (error) {
                  alert(error.message);
                }
              }} className="save-button">Save CTA</button>
              <button onClick={() => setActiveGroupSection(null)} className="btn-submit-small" style={{marginLeft: '1rem', background: 'rgba(255, 77, 0, 0.2)', border: '2px solid var(--primary)'}}>
                ← Back to Sections
              </button>
            </div>
          </div>
        )}

        {/* Group Page - Gallery Section */}
        {activeTab === 'group-page' && activeGroupSection === 'gallery' && (
          <div className="admin-section">
            <div style={{display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '2rem'}}>
              <ArrowLeft 
                size={32} 
                style={{cursor: 'pointer', color: 'var(--primary)'}} 
                onClick={() => setActiveGroupSection(null)}
              />
              <h2 className="section-title-admin" style={{marginBottom: 0}}>Group Page - Gallery</h2>
            </div>
            <div className="admin-card">
              <h3 style={{marginBottom: '1.5rem'}}>Gallery Images</h3>
              <p style={{color: 'rgba(245, 245, 245, 0.7)', marginBottom: '2rem'}}>
                Manage gallery images for the Group Training page.
              </p>
              {groupGallery.length > 0 ? (
                <div className="gallery-grid">
                  {groupGallery.map((item) => (
                    <div key={item.id} className="gallery-item">
                      <img src={item.imageUrl} alt={item.alt || 'Group Gallery'} className="gallery-image" />
                      <button 
                        onClick={async () => {
                          if (window.confirm('Delete this image?')) {
                            try {
                              await deleteDoc(doc(db, 'groupGallery', item.id));
                              alert('Image deleted!');
                              fetchAllData();
                            } catch (error) {
                              alert(error.message);
                            }
                          }
                        }}
                        className="btn-delete"
                        style={{marginTop: '1rem', width: '100%'}}
                      >
                        Delete
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{color: 'rgba(245, 245, 245, 0.6)', marginBottom: '2rem'}}>No gallery images yet.</p>
              )}
              <div style={{marginTop: '2rem'}}>
                <h4 style={{marginBottom: '1rem'}}>Add New Image</h4>
                <ImageUpload
                  onUploadComplete={async (url) => {
                    try {
                      await addDoc(collection(db, 'groupGallery'), {
                        imageUrl: url,
                        alt: 'Group Training',
                        order: groupGallery.length,
                        createdAt: serverTimestamp()
                      });
                      alert('Image added!');
                      fetchAllData();
                    } catch (error) {
                      alert(error.message);
                    }
                  }}
                  folder="group-gallery"
                />
              </div>
              <button onClick={() => setActiveGroupSection(null)} className="btn-submit" style={{marginTop: '2rem'}}>
                ← Back to Sections
              </button>
            </div>
          </div>
        )}

        {/* Mobile Page - Section Grid */}
        {activeTab === 'mobile-page' && !activeMobileSection && (
          <div className="admin-section">
            <h2 className="section-title-admin">Mobile Page Sections</h2>
            <div className="main-sections-grid">
              <div className="section-card" onClick={() => setActiveMobileSection('hero')}>
                <h3>Hero Section</h3>
                <p>Page header and CTA</p>
              </div>
              <div className="section-card" onClick={() => setActiveMobileSection('video')}>
                <h3>YouTube Video</h3>
                <p>Featured video at top</p>
              </div>
              <div className="section-card" onClick={() => setActiveMobileSection('whofor')}>
                <h3>Who It's For</h3>
                <p>Target audience section</p>
              </div>
              <div className="section-card" onClick={() => setActiveMobileSection('packages')}>
                <h3>Training Packages</h3>
                <p>Manage package offerings</p>
              </div>
              <div className="section-card" onClick={() => setActiveMobileSection('gallery')}>
                <h3>Gallery</h3>
                <p>Photo gallery for page</p>
              </div>
              <div className="section-card" onClick={() => setActiveMobileSection('cta')}>
                <h3>Final CTA</h3>
                <p>Bottom call-to-action</p>
              </div>
            </div>
          </div>
        )}

        {/* Mobile Page - Hero Section */}
        {activeTab === 'mobile-page' && activeMobileSection === 'hero' && (
          <div className="admin-section">
            <div style={{display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '2rem'}}>
              <ArrowLeft 
                size={32} 
                style={{cursor: 'pointer', color: 'var(--primary)'}} 
                onClick={() => setActiveMobileSection(null)}
              />
              <h2 className="section-title-admin" style={{marginBottom: 0}}>Mobile Page - Hero Section</h2>
            </div>
            
            <div className="admin-card" style={{marginBottom: '2rem'}}>
              <div className="form-group">
                <label className="form-label">Page Title</label>
                <input
                  type="text"
                  value={mobilePageHero.title || ''}
                  onChange={(e) => setMobilePageHero({...mobilePageHero, title: e.target.value})}
                  className="form-input"
                  placeholder="MOBILE TRAINING"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Subtitle</label>
                <input
                  type="text"
                  value={mobilePageHero.subtitle || ''}
                  onChange={(e) => setMobilePageHero({...mobilePageHero, subtitle: e.target.value})}
                  className="form-input"
                  placeholder="We Come To You"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Button Text</label>
                <input
                  type="text"
                  value={mobilePageHero.buttonText || ''}
                  onChange={(e) => setMobilePageHero({...mobilePageHero, buttonText: e.target.value})}
                  className="form-input"
                  placeholder="View Packages"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Hero background video (MP4)</label>
                <p style={{ marginBottom: '0.75rem', fontSize: '0.85rem', color: 'rgba(245,245,245,0.65)' }}>
                  Upload an MP4 or paste a URL. Leave empty for a solid black hero.
                </p>
                <VideoUpload
                  folder="mobile-hero-background"
                  currentVideoUrl={mobilePageHero.backgroundVideoUrl || ''}
                  onUploadComplete={(url) => setMobilePageHero({ ...mobilePageHero, backgroundVideoUrl: url })}
                />
                <label className="form-label" style={{ marginTop: '1rem' }}>Video URL (optional)</label>
                <input
                  type="url"
                  value={mobilePageHero.backgroundVideoUrl || ''}
                  onChange={(e) => setMobilePageHero({ ...mobilePageHero, backgroundVideoUrl: e.target.value })}
                  className="form-input"
                  placeholder="https://…"
                />
              </div>
              <button onClick={async () => {
                try {
                  await setDoc(doc(db, 'settings', 'mobileHero'), {
                    ...mobilePageHero,
                    backgroundVideoUrl: sanitizeURL(mobilePageHero.backgroundVideoUrl || ''),
                    updatedAt: serverTimestamp()
                  });
                  alert('Mobile hero updated!');
                } catch (error) {
                  alert(error.message);
                }
              }} className="save-button">Save Hero</button>
              <button onClick={() => setActiveMobileSection(null)} className="btn-submit-small" style={{marginLeft: '1rem', background: 'rgba(255, 77, 0, 0.2)', border: '2px solid var(--primary)'}}>
                ← Back to Sections
              </button>
            </div>
          </div>
        )}

        {/* Mobile Page - Video Section */}
        {activeTab === 'mobile-page' && activeMobileSection === 'video' && (
          <div className="admin-section">
            <div style={{display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '2rem'}}>
              <ArrowLeft 
                size={32} 
                style={{cursor: 'pointer', color: 'var(--primary)'}} 
                onClick={() => setActiveMobileSection(null)}
              />
              <h2 className="section-title-admin" style={{marginBottom: 0}}>Mobile Page - YouTube Video</h2>
            </div>
            <div className="admin-card" style={{marginBottom: '2rem'}}>
              <div className="form-group">
                <label className="form-label">YouTube URL</label>
                <input
                  type="url"
                  value={mobilePageVideo.url || ''}
                  onChange={(e) => setMobilePageVideo({...mobilePageVideo, url: e.target.value})}
                  className="form-input"
                  placeholder="https://www.youtube.com/watch?v=..."
                />
                <small style={{display: 'block', marginTop: '0.5rem', color: 'rgba(245, 245, 245, 0.6)'}}>
                  Paste full YouTube URL
                </small>
              </div>
              <div className="form-group">
                <label className="form-label">Video Title (Optional)</label>
                <input
                  type="text"
                  value={mobilePageVideo.title || ''}
                  onChange={(e) => setMobilePageVideo({...mobilePageVideo, title: e.target.value})}
                  className="form-input"
                  placeholder="Mobile Training Experience"
                />
              </div>
              <button onClick={async () => {
                try {
                  await setDoc(doc(db, 'settings', 'mobileVideo'), {...mobilePageVideo, updatedAt: serverTimestamp()});
                  alert('Video updated!');
                } catch (error) {
                  alert(error.message);
                }
              }} className="save-button">Save Video</button>
              <button onClick={() => setActiveMobileSection(null)} className="btn-submit-small" style={{marginLeft: '1rem', background: 'rgba(255, 77, 0, 0.2)', border: '2px solid var(--primary)'}}>
                ← Back to Sections
              </button>
            </div>
          </div>
        )}

        {/* Mobile Page - Who For Section */}
        {activeTab === 'mobile-page' && activeMobileSection === 'whofor' && (
          <div className="admin-section">
            <div style={{display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '2rem'}}>
              <ArrowLeft 
                size={32} 
                style={{cursor: 'pointer', color: 'var(--primary)'}} 
                onClick={() => setActiveMobileSection(null)}
              />
              <h2 className="section-title-admin" style={{marginBottom: 0}}>Mobile Page - Who It's For</h2>
            </div>
            <div className="admin-card" style={{marginBottom: '2rem'}}>
              <div className="form-group">
                <label className="form-label">Title</label>
                <input
                  type="text"
                  value={mobilePageWhoFor.title || ''}
                  onChange={(e) => setMobilePageWhoFor({...mobilePageWhoFor, title: e.target.value})}
                  className="form-input"
                  placeholder="Who Is This For?"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Content</label>
                <textarea
                  value={mobilePageWhoFor.content || ''}
                  onChange={(e) => setMobilePageWhoFor({...mobilePageWhoFor, content: e.target.value})}
                  className="form-textarea"
                  rows="6"
                  placeholder="Describe who mobile training is perfect for..."
                ></textarea>
              </div>
              <div className="form-group">
                <label className="form-label">Features/Bullet Points (one per line)</label>
                <textarea
                  value={Array.isArray(mobilePageWhoFor.features) ? mobilePageWhoFor.features.join('\n') : (mobilePageWhoFor.features || '')}
                  onChange={(e) => setMobilePageWhoFor({...mobilePageWhoFor, features: e.target.value})}
                  className="form-textarea"
                  rows="5"
                  placeholder="Busy professionals&#10;Work from home&#10;No gym needed&#10;Flexible scheduling"
                ></textarea>
                <small style={{display: 'block', marginTop: '0.5rem', color: 'rgba(245, 245, 245, 0.6)'}}>
                  Enter one feature per line. Press Enter to create a new line.
                </small>
              </div>
              <div className="form-group">
                <label className="form-label">Image</label>
                <ImageUpload
                  currentImageUrl={mobilePageWhoFor.imageUrl}
                  onUploadComplete={(url) => setMobilePageWhoFor({...mobilePageWhoFor, imageUrl: url})}
                  folder="mobile-page"
                />
              </div>
              <button onClick={async () => {
                try {
                  const featuresToSave = typeof mobilePageWhoFor.features === 'string' 
                    ? mobilePageWhoFor.features.split('\n').filter(f => f.trim())
                    : mobilePageWhoFor.features;
                  await setDoc(doc(db, 'settings', 'mobileWhoFor'), {
                    ...mobilePageWhoFor, 
                    features: featuresToSave,
                    updatedAt: serverTimestamp()
                  });
                  alert('Updated!');
                } catch (error) {
                  alert(error.message);
                }
              }} className="save-button">Save Section</button>
              <button onClick={() => setActiveMobileSection(null)} className="btn-submit-small" style={{marginLeft: '1rem', background: 'rgba(255, 77, 0, 0.2)', border: '2px solid var(--primary)'}}>
                ← Back to Sections
              </button>
            </div>
          </div>
        )}

        {/* Mobile Page - Packages Section */}
        {activeTab === 'mobile-page' && activeMobileSection === 'packages' && (
          <div className="admin-section">
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem'}}>
              <div style={{display: 'flex', alignItems: 'center', gap: '1rem'}}>
                <ArrowLeft 
                  size={32} 
                  style={{cursor: 'pointer', color: 'var(--primary)'}} 
                  onClick={() => setActiveMobileSection(null)}
                />
                <h2 className="section-title-admin" style={{marginBottom: 0}}>Mobile Page - Training Packages ({mobilePackages.length})</h2>
              </div>
              <button 
                onClick={() => setShowNewMobilePackageForm(!showNewMobilePackageForm)} 
                className="btn-submit-small"
                style={{marginLeft: 'auto'}}
              >
                {showNewMobilePackageForm ? 'Cancel' : '+ Add New Package'}
              </button>
            </div>

            {showNewMobilePackageForm && (
              <div className="admin-card" style={{marginBottom: '2rem'}}>
                <h3 className="card-title">Add New Package</h3>
                <form onSubmit={handleAddMobilePackage}>
                  <div className="form-group">
                    <label className="form-label">Title *</label>
                    <input
                      type="text"
                      value={newMobilePackage.title}
                      onChange={(e) => setNewMobilePackage({...newMobilePackage, title: e.target.value})}
                      className="form-input"
                      placeholder="e.g., Mobile Training Package"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Price</label>
                    <input
                      type="text"
                      value={newMobilePackage.price}
                      onChange={(e) => setNewMobilePackage({...newMobilePackage, price: e.target.value})}
                      className="form-input"
                      placeholder="e.g., $150/month"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Description</label>
                    <textarea
                      value={newMobilePackage.description}
                      onChange={(e) => setNewMobilePackage({...newMobilePackage, description: e.target.value})}
                      className="form-textarea"
                      rows="4"
                      placeholder="Package description..."
                    ></textarea>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Features (one per line)</label>
                    <textarea
                      value={newMobilePackage.features}
                      onChange={(e) => setNewMobilePackage({...newMobilePackage, features: e.target.value})}
                      className="form-textarea"
                      rows="5"
                      placeholder="Train anywhere&#10;Flexible schedule&#10;Personalized workouts"
                    ></textarea>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Calendly URL</label>
                    <input
                      type="url"
                      value={newMobilePackage.calendlyUrl}
                      onChange={(e) => setNewMobilePackage({...newMobilePackage, calendlyUrl: e.target.value})}
                      className="form-input"
                      placeholder="https://calendly.com/..."
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Display Order</label>
                    <input
                      type="number"
                      value={newMobilePackage.order}
                      onChange={(e) => setNewMobilePackage({...newMobilePackage, order: parseInt(e.target.value) || 0})}
                      className="form-input"
                      placeholder="0"
                    />
                  </div>
                  <button type="submit" className="btn-submit">Add Package</button>
                </form>
              </div>
            )}

            {mobilePackages.length > 0 ? (
              <div className="services-admin-grid">
                {mobilePackages.map((pkg) => (
                  <div key={pkg.id} className="admin-card">
                    <h3 className="card-title">{pkg.title}</h3>
                    <div className="form-group">
                      <label className="form-label">Title</label>
                      <input
                        type="text"
                        value={pkg.title || ''}
                        onChange={(e) => handleMobilePackageUpdate(pkg.id, 'title', e.target.value)}
                        className="form-input"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Price</label>
                      <input
                        type="text"
                        value={pkg.price || ''}
                        onChange={(e) => handleMobilePackageUpdate(pkg.id, 'price', e.target.value)}
                        className="form-input"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Description</label>
                      <textarea
                        value={pkg.description || ''}
                        onChange={(e) => handleMobilePackageUpdate(pkg.id, 'description', e.target.value)}
                        className="form-textarea"
                        rows="4"
                      ></textarea>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Features (one per line)</label>
                      <textarea
                        value={Array.isArray(pkg.features) ? pkg.features.join('\n') : (pkg.features || '')}
                        onChange={(e) => handleMobilePackageUpdate(pkg.id, 'features', e.target.value)}
                        className="form-textarea"
                        rows="5"
                      ></textarea>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Calendly URL</label>
                      <input
                        type="url"
                        value={pkg.calendlyUrl || ''}
                        onChange={(e) => handleMobilePackageUpdate(pkg.id, 'calendlyUrl', e.target.value)}
                        className="form-input"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Display Order</label>
                      <input
                        type="number"
                        value={pkg.order || 0}
                        onChange={(e) => handleMobilePackageUpdate(pkg.id, 'order', parseInt(e.target.value) || 0)}
                        className="form-input"
                      />
                    </div>
                    <div style={{display: 'flex', gap: '1rem'}}>
                      <button onClick={() => handleMobilePackageSave(pkg.id)} className="btn-submit-small" style={{flex: 1}}>
                        Save Changes
                      </button>
                      <button 
                        onClick={() => handleDeleteMobilePackage(pkg.id)} 
                        className="btn-submit-small"
                        style={{
                          flex: 1,
                          background: 'linear-gradient(135deg, #ff6b6b 0%, #ff4757 100%)'
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="admin-card">
                <p style={{color: 'rgba(245, 245, 245, 0.7)'}}>
                  No packages found. Click "+ Add New Package" to create your first package.
                </p>
              </div>
            )}
            <button onClick={() => setActiveMobileSection(null)} className="btn-submit" style={{marginTop: '2rem'}}>
              ← Back to Sections
            </button>
          </div>
        )}

        {/* Mobile Page - CTA Section */}
        {activeTab === 'mobile-page' && activeMobileSection === 'cta' && (
          <div className="admin-section">
            <div style={{display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '2rem'}}>
              <ArrowLeft 
                size={32} 
                style={{cursor: 'pointer', color: 'var(--primary)'}} 
                onClick={() => setActiveMobileSection(null)}
              />
              <h2 className="section-title-admin" style={{marginBottom: 0}}>Mobile Page - Final CTA</h2>
            </div>
            <div className="admin-card" style={{marginBottom: '2rem'}}>
              <h3 style={{marginBottom: '1.5rem'}}>Call to Action</h3>
              <div className="form-group">
                <label className="form-label">Title</label>
                <input
                  type="text"
                  value={mobilePageFinalCTA.title || ''}
                  onChange={(e) => setMobilePageFinalCTA({...mobilePageFinalCTA, title: e.target.value})}
                  className="form-input"
                  placeholder="Ready to Start?"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Subtitle</label>
                <input
                  type="text"
                  value={mobilePageFinalCTA.subtitle || ''}
                  onChange={(e) => setMobilePageFinalCTA({...mobilePageFinalCTA, subtitle: e.target.value})}
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Calendly URL</label>
                <input
                  type="url"
                  value={mobilePageFinalCTA.calendlyUrl || ''}
                  onChange={(e) => setMobilePageFinalCTA({...mobilePageFinalCTA, calendlyUrl: e.target.value})}
                  className="form-input"
                  placeholder="https://calendly.com/bodyworksmiami/mobile-training"
                />
              </div>
              <button onClick={async () => {
                try {
                  await setDoc(doc(db, 'settings', 'mobileFinalCTA'), {...mobilePageFinalCTA, updatedAt: serverTimestamp()});
                  alert('Updated!');
                } catch (error) {
                  alert(error.message);
                }
              }} className="save-button">Save CTA</button>
              <button onClick={() => setActiveMobileSection(null)} className="btn-submit-small" style={{marginLeft: '1rem', background: 'rgba(255, 77, 0, 0.2)', border: '2px solid var(--primary)'}}>
                ← Back to Sections
              </button>
            </div>
          </div>
        )}

        {/* Mobile Page - Gallery Section */}
        {activeTab === 'mobile-page' && activeMobileSection === 'gallery' && (
          <div className="admin-section">
            <div style={{display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '2rem'}}>
              <ArrowLeft 
                size={32} 
                style={{cursor: 'pointer', color: 'var(--primary)'}} 
                onClick={() => setActiveMobileSection(null)}
              />
              <h2 className="section-title-admin" style={{marginBottom: 0}}>Mobile Page - Gallery</h2>
            </div>
            <div className="admin-card">
              <h3 style={{marginBottom: '1.5rem'}}>Gallery Images</h3>
              <p style={{color: 'rgba(245, 245, 245, 0.7)', marginBottom: '2rem'}}>
                Manage gallery images for the Mobile Training page.
              </p>
              {mobileGallery.length > 0 ? (
                <div className="gallery-grid">
                  {mobileGallery.map((item) => (
                    <div key={item.id} className="gallery-item">
                      <img src={item.imageUrl} alt={item.alt || 'Mobile Gallery'} className="gallery-image" />
                      <button 
                        onClick={async () => {
                          if (window.confirm('Delete this image?')) {
                            try {
                              await deleteDoc(doc(db, 'mobileGallery', item.id));
                              alert('Image deleted!');
                              fetchAllData();
                            } catch (error) {
                              alert(error.message);
                            }
                          }
                        }}
                        className="btn-delete"
                        style={{marginTop: '1rem', width: '100%'}}
                      >
                        Delete
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{color: 'rgba(245, 245, 245, 0.6)', marginBottom: '2rem'}}>No gallery images yet.</p>
              )}
              <div style={{marginTop: '2rem'}}>
                <h4 style={{marginBottom: '1rem'}}>Add New Image</h4>
                <ImageUpload
                  onUploadComplete={async (url) => {
                    try {
                      await addDoc(collection(db, 'mobileGallery'), {
                        imageUrl: url,
                        alt: 'Mobile Training',
                        order: mobileGallery.length,
                        createdAt: serverTimestamp()
                      });
                      alert('Image added!');
                      fetchAllData();
                    } catch (error) {
                      alert(error.message);
                    }
                  }}
                  folder="mobile-gallery"
                />
              </div>
              <button onClick={() => setActiveMobileSection(null)} className="btn-submit" style={{marginTop: '2rem'}}>
                ← Back to Sections
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Admin;
